import React, { useState, useContext, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Card,
  Button,
  Form,
  Input,
  Select,
  Radio,
  Checkbox,
  Table,
  Space,
  Typography,
  Row,
  Col,
  Alert,
  Divider,
  Tag,
  Avatar,
  Modal,
  DatePicker,
  TimePicker,
  Switch,
  Statistic,
  Badge,
  message,
  Tooltip,
  Empty,
  Spin
} from 'antd';
import {
  ArrowLeftOutlined,
  SendOutlined,
  UserOutlined,
  TeamOutlined,
  FileTextOutlined,
  FilterOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  ReloadOutlined,
  SearchOutlined,
  HeartOutlined,
  UsergroupAddOutlined,
  HomeOutlined,
  BankOutlined,
  StarOutlined,
  TagOutlined,
  CloseOutlined,
  BookOutlined,
  LinkOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { AuthContext } from '../../../../context/authContext';
import axiosInstance from '../../../../config/axiosConfig';
import axiosConfig from '../../../../config/axiosConfig';
import moment from 'moment';
import './AdvancedBroadcastBuilder.css';
import './AdvancedTemplateBuilder.css';
import { calculateMessageCost, getRateBreakdown, preloadRates } from '../../../../utils/rateUtils';
import PricingCalculator from './PricingCalculator';
import { AudienceSourceSelector, ByoWebhookConfigurator } from '../../../../components/audience';
import { useFeatureFlag, FEATURE_KEYS } from '../../../../utils/planFeatures';
import CampaignAIBehaviourPanel from './CampaignAIBehaviourPanel';
import { aiConfigApi } from '../../../aiChatbot/services/aiChatbotApi';
import TemplateWizardStepIndicator from '../../../email/templates/TemplateWizardStepIndicator';

const { Option } = Select;
const { Text, Title } = Typography;
const { TextArea } = Input;

const AdvancedBroadcastBuilder = ({ onBack, onSave }) => {
  // Skeletal Loading Components with prefers-reduced-motion support
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const skeletonAnimation = prefersReducedMotion ? 'none' : 'broadcast-skeleton-loading 1s infinite ease-in-out';

  const AddressBookSkeleton = () => (
    <div style={{ padding: 16 }}>
      {Array.from({ length: 5 }).map((_, rowIndex) => (
        <div key={rowIndex} style={{
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          padding: '12px 0',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <div style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            animation: skeletonAnimation
          }} />
          <div style={{ flex: 1 }}>
            <div style={{
              height: 16,
              background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
              borderRadius: 4,
              marginBottom: 6,
              width: `${60 + Math.random() * 30}%`,
              animation: skeletonAnimation
            }} />
            <div style={{
              height: 12,
              background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
              borderRadius: 4,
              width: `${40 + Math.random() * 40}%`,
              animation: skeletonAnimation
            }} />
          </div>
        </div>
      ))}
    </div>
  );

  const TemplateSkeleton = () => (
    <div style={{ padding: 16 }}>
      {Array.from({ length: 3 }).map((_, index) => (
        <Card key={index} style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <div style={{
              width: 60,
              height: 40,
              borderRadius: 6,
              background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
              animation: skeletonAnimation
            }} />
            <div style={{ flex: 1 }}>
              <div style={{
                height: 16,
                background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                borderRadius: 4,
                marginBottom: 8,
                animation: skeletonAnimation
              }} />
              <div style={{
                height: 12,
                background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                borderRadius: 4,
                width: '70%',
                animation: skeletonAnimation
              }} />
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  const ContactsSkeleton = () => (
    <div style={{ border: '1px solid #f0f0f0', borderRadius: 8 }}>
      <div style={{
        padding: '12px 16px',
        background: '#fafafa',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        gap: 16
      }}>
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} style={{
            height: 20,
            background: 'linear-gradient(90deg, #e0e0e0 25%, #d0d0d0 50%, #e0e0e0 75%)',
            borderRadius: 4,
            flex: '1',
            animation: skeletonAnimation
          }} />
        ))}
      </div>
      {Array.from({ length: 5 }).map((_, rowIndex) => (
        <div key={rowIndex} style={{
          padding: '12px 16px',
          borderBottom: rowIndex < 4 ? '1px solid #f0f0f0' : 'none',
          display: 'flex',
          gap: 16,
          alignItems: 'center'
        }}>
          {Array.from({ length: 4 }).map((_, colIndex) => (
            <div key={colIndex} style={{
              height: 16,
              background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
              borderRadius: 4,
              flex: '1',
              width: `${50 + Math.random() * 40}%`,
              animation: skeletonAnimation
            }} />
          ))}
        </div>
      ))}
    </div>
  );

  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const { currentUser } = useContext(AuthContext);
  
  // Campaign State
  const [campaignData, setCampaignData] = useState({
    name: '',
    description: '',
    segment: {
      type: 'all', // 'all', 'custom', 'saved'
      name: '',
      filters: {
        category: [],
        state: [],
        city: [],
        labels: []
      },
      selectedContacts: []
    },
    template: {
      id: null,
      name: '',
      content: '',
      variables: [],
      variableSamples: {},
      type: 'existing' // 'existing', 'create'
    },
    schedule: {
      type: 'immediate', // 'immediate', 'scheduled', 'recurring'
      date: null,
      time: null,
      repeat: 'none' // 'none', 'daily', 'weekly', 'monthly'
    }
  });

  // UI State
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [segments, setSegments] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const [allContactsSelected, setAllContactsSelected] = useState(false);
  const [validationTriggered, setValidationTriggered] = useState(false);
  
  // Add state to track total selected contacts across all pages
  const [totalSelectedContacts, setTotalSelectedContacts] = useState([]);
  const [allSelectedContactIds, setAllSelectedContactIds] = useState(new Set());

  // Phase 2.5 — Audience source selector (addressbook | byo_webhook)
  const [audienceSource, setAudienceSource] = useState('addressbook');
  const [byoConfig, setByoConfig] = useState({
    url: '',
    auth: { type: 'none' },
    hard_send_cap: 10000,
    saved_preset_id: null,
    mapping: {},
  });
  const { enabled: byoEnabled } = useFeatureFlag(FEATURE_KEYS.BYO_WEBHOOK);

  // Per-campaign AI behaviour override (skill scoping, no-reply). The panel
  // itself loads the available skills + KB tags; we just own the value.
  const [aiBehaviour, setAiBehaviour] = useState({
    skill_keys: [],
    kb_tags: [],
    is_one_way: false,
  });
  
  // Plan-based quota state
  const [planStatus, setPlanStatus] = useState(null);
  
  // Address book and variable mapping state
  const [addressBooks, setAddressBooks] = useState([]);
  const [currentAddressBook, setCurrentAddressBook] = useState(null);
  const [addressBookLoading, setAddressBookLoading] = useState(false);
  const [contactColumns, setContactColumns] = useState([]);
  const [templateMapping, setTemplateMapping] = useState({
    variabletype: 'number', // 'number' for {{1}}, {{2}} or 'name' for {{name}}, {{xyz}}
    mapping: {}, // e.g., {"{{1}}": "name", "{{2}}": "phone"}
    headerMediaColumn: '', // addressbook column key whose value is the per-contact media URL
    headerMediaType: '',   // 'document' | 'image' | 'video' (detected from template header)
  });
  
  // Template preview state
  const [templatePreviewVisible, setTemplatePreviewVisible] = useState(false);
  const [selectedTemplatePreview, setSelectedTemplatePreview] = useState(null);
  
  // Pagination and filters
  const [contactsLoading, setContactsLoading] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalContacts, setTotalContacts] = useState(0);
  const [templateSearchText, setTemplateSearchText] = useState('');
  
  // Rate management state
  const [pricePerMessage, setPricePerMessage] = useState(0);
  const [rateLoading, setRateLoading] = useState(false);
  
  // Rate calculation state for estimated cost
  const [estimatedCost, setEstimatedCost] = useState(null);
  const [rateDetails, setRateDetails] = useState(null);
  const [rateCalculationLoading, setRateCalculationLoading] = useState(false);

const [pricingCalcVisible, setPricingCalcVisible] = useState(false);

// Add new states for the discount modal
const [discountModalVisible, setDiscountModalVisible] = useState(false);
const [discountsData, setDiscountsData] = useState([]);
const [selectedCategory, setSelectedCategory] = useState('Utility');
const [selectedCountryCode, setSelectedCountryCode] = useState(null);
const [discountLoading, setDiscountLoading] = useState(false);

// Memoized country options
const countryOptions = useMemo(() => {
  const uniqueCountries = [...new Set(discountsData.map(d => d.countrycode))];
  return uniqueCountries.map(code => ({
    value: code,
    label: discountsData.find(d => d.countrycode === code)?.country || code
  }));
}, [discountsData]);

// Memoized filtered rows
const filteredDiscountRows = useMemo(() => {
  return discountsData
    .filter(d => d.category === selectedCategory && d.countrycode === selectedCountryCode)
    .sort((a, b) => a.startrange - b.startrange)
    .map(d => ({
      from: d.startrange.toLocaleString(),
      to: d.endrange ? d.endrange.toLocaleString() : '∞',
      rate: d.rate,
      discountrate: d.discountrate,
      versus: `-${d.percentage}%`,
      currency: '₹' // Always use Indian Rupee symbol for all countries
    }));
}, [selectedCategory, selectedCountryCode, discountsData]);
  
  // Filters for contacts
  const [contactFilters, setContactFilters] = useState({
    search: '',
    mobileno: '',
    usercategory: '',
    state: '',
    city: ''
  });

  // Search functionality
  const [searchText, setSearchText] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeoutRef = useRef(null);

  // State and City management
  const [statesList, setStatesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [citiesLoading, setCitiesLoading] = useState(false);

  // Label Management States
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [availableLabels, setAvailableLabels] = useState([]);

  // Filter States
  const [nameFilter, setNameFilter] = useState('');
  const [mobileFilter, setMobileFilter] = useState('');
  const [labelFilter, setLabelFilter] = useState([]);
  const [filterModalVisible, setFilterModalVisible] = useState(false);

  // Mock data - Replace with actual API calls
  const mockContacts = [
    {
      id: 1,
      name: 'John Doe',
      phone: '+91 9876543210',
      email: 'john@example.com',
      category: 'Premium',
      state: 'Maharashtra',
      city: 'Mumbai',
      labels: ['VIP', 'Active']
    },
    {
      id: 2,
      name: 'Jane Smith',
      phone: '+91 9876543211',
      email: 'jane@example.com',
      category: 'General',
      state: 'Delhi',
      city: 'New Delhi',
      labels: ['New', 'Potential']
    },
    {
      id: 3,
      name: 'Bob Johnson',
      phone: '+91 9876543212',
      email: 'bob@example.com',
      category: 'VIP',
      state: 'Karnataka',
      city: 'Bangalore',
      labels: ['VIP', 'Loyal']
    }
  ];

  // Predefined labels with colors and icons (same as ContactsPage)
  const predefinedLabels = [
    { 
      value: 'favourite', 
      label: 'Favourite', 
      color: '#ff4d4f', 
      icon: <HeartOutlined />,
      description: 'Mark as favourite contact'
    },
    { 
      value: 'close', 
      label: 'Close Friend', 
      color: '#52c41a', 
      icon: <StarOutlined />,
      description: 'Close friend or important contact'
    },
    { 
      value: 'family', 
      label: 'Family', 
      color: '#1890ff', 
      icon: <HomeOutlined />,
      description: 'Family member'
    },
    { 
      value: 'business', 
      label: 'Business', 
      color: '#722ed1', 
      icon: <TagOutlined />,
      description: 'Business contact'
    },
    { 
      value: 'vip', 
      label: 'VIP Customer', 
      color: '#fa8c16', 
      icon: <StarOutlined />,
      description: 'VIP or premium customer'
    }
  ];

  useEffect(() => {
    fetchAddressBooks(); // Fetch address books on component mount
    fetchTemplates();
    fetchStates(); // Fetch states list on component mount
  }, []);

  // Fetch contacts when address book changes
  useEffect(() => {
    if (currentAddressBook) {
      fetchContactsFromAddressBook({ 
        search: searchText, 
        labelfilter: selectedLabels 
      }, currentPage, pageSize);
    }
  }, [currentAddressBook]);

  // Handle search and label filter changes
  useEffect(() => {
    if (currentAddressBook) {
      const timeoutId = setTimeout(() => {
        fetchContactsFromAddressBook({ 
          search: searchText, 
          labelfilter: selectedLabels 
        }, 1, pageSize);
        setCurrentPage(1);
      }, 300); // Debounce search

      return () => clearTimeout(timeoutId);
    }
  }, [searchText, selectedLabels]);

  useEffect(() => {
    // Sync selectedRowKeys with current page contacts that are in total selected
    // Only update if there's actually a difference to prevent unnecessary re-renders
    const currentPageSelectedIds = filteredContacts
      .filter(contact => allSelectedContactIds.has(contact.userid || contact.id))
      .map(contact => contact.userid || contact.id);
    
    // Check if the arrays are different before updating
    const currentKeys = [...selectedRowKeys].sort();
    const newKeys = [...currentPageSelectedIds].sort();
    const isDifferent = currentKeys.length !== newKeys.length || 
                       currentKeys.some((key, index) => key !== newKeys[index]);
    
    if (isDifferent) {
      setSelectedRowKeys(newKeys);
    }
  }, [filteredContacts, allSelectedContactIds]); // Removed selectedRowKeys to prevent infinite loop

  // Update template mapping when template changes
  useEffect(() => {
    const selectedTemplate = templates.find(t => t.templateid === campaignData.template.id);
    if (selectedTemplate) {
      const templateVariables = extractTemplateVariables(selectedTemplate);
      const variableType = determineVariableType(templateVariables);
      const headerType = getTemplateHeaderType(selectedTemplate);
      
      if (templateVariables.length > 0 || headerType) {
        setTemplateMapping(prev => ({
          ...prev,
          variabletype: variableType,
          // Reset header media column when template changes to avoid stale mapping
          headerMediaColumn: prev.headerMediaType !== headerType ? '' : prev.headerMediaColumn,
          headerMediaType: headerType || '',
        }));
      }
    }
  }, [campaignData.template.id, templates]);

  // Fetch plan status on component mount
  const fetchWalletBalance = async () => {
    try {
      const planRes = await axiosConfig.get('/wallet/plan-status');
      const entity = planRes.data?.entity;
      if (entity && entity.has_active_plan !== undefined) {
        setPlanStatus(entity);
      }
    } catch (error) {
      console.error('Error fetching plan status:', error);
    }
  };

  useEffect(() => {
    fetchWalletBalance();
    loadMessageRate();
  }, []);

  // Load message rate based on template category
  const loadMessageRate = async () => {
    try {
      setRateLoading(true);
      const selectedTemplate = templates.find(t => t.templateid === campaignData.template.id);
      const templateCategory = selectedTemplate?.templatecategory || 'MARKETING';
      const cost = await calculateMessageCost(templateCategory);
      setPricePerMessage(cost);
    } catch (error) {
      console.error('Error loading message rate:', error);
      setPricePerMessage(0.5); // Fallback rate
    } finally {
      setRateLoading(false);
    }
  };

  // Reload rates when template changes
  useEffect(() => {
    if (campaignData.template.id) {
      loadMessageRate();
    }
  }, [campaignData.template.id, templates]);

  // Utility Functions
  const normalizeContactDataWithUserData = (contacts) => {
    return contacts.map(contact => {
      const normalized = { ...contact };
      
      // If userdata exists and is a string, try to parse it
      if (contact.userdata && typeof contact.userdata === 'string') {
        try {
          const parsedUserData = JSON.parse(contact.userdata);
          // Flatten userdata fields with userdata_ prefix
          Object.keys(parsedUserData).forEach(key => {
            normalized[`userdata_${key}`] = parsedUserData[key];
          });
        } catch (e) {
          console.warn('Failed to parse userdata for contact:', contact.userid);
        }
      } else if (contact.userdata && typeof contact.userdata === 'object') {
        // If userdata is already an object, flatten it
        Object.keys(contact.userdata).forEach(key => {
          normalized[`userdata_${key}`] = contact.userdata[key];
        });
      }
      
      return normalized;
    });
  };

  const extractLabelsFromContacts = (contacts) => {
    const labelSet = new Set();
    
    contacts.forEach(contact => {
      // Check various label fields - including 'label' from API response
      const labelFields = ['label', 'labels', 'userdata_labels', 'userlabels'];
      
      labelFields.forEach(field => {
        if (contact[field]) {
          let labels = [];
          
          try {
            if (typeof contact[field] === 'string') {
              // Handle JSON array string like ["favourite","close"]
              if (contact[field].startsWith('[') && contact[field].endsWith(']')) {
                labels = JSON.parse(contact[field]).filter(Boolean);
              }
              // Handle comma-separated string or single label
              else {
                labels = contact[field].split(',').map(label => label.trim()).filter(Boolean);
              }
            } else if (Array.isArray(contact[field])) {
              // Handle array
              labels = contact[field].filter(Boolean);
            }
          } catch (e) {
            // If JSON parsing fails, treat as regular string
            if (typeof contact[field] === 'string') {
              labels = contact[field].split(',').map(label => label.trim()).filter(Boolean);
            }
          }
          
          labels.forEach(label => labelSet.add(label));
        }
      });
    });
    
    return Array.from(labelSet).sort();
  };

  // Fetch all labels from address book using dedicated labels API
  const fetchAllLabelsFromAddressBook = async () => {
    if (!currentAddressBook?.bookid) {
      setAvailableLabels([]);
      return;
    }

    try {
      const response = await axiosConfig.post('/addressbook/labels', {
        bookid: currentAddressBook.bookid
      });

      if (
        (response.data?.status === true || response.data?.statuscode === 200) &&
        response.data?.entity?.labels
      ) {
        const labels = response.data.entity.labels;
        setAvailableLabels(Array.isArray(labels) ? labels.sort() : []);
      } else {
        setAvailableLabels([]);
      }
    } catch (error) {
      console.error('Error fetching labels from API:', error);
      // Fallback: extract labels from currently loaded contacts
      if (contacts.length > 0) {
        const fallbackLabels = extractLabelsFromContacts(contacts);
        setAvailableLabels(fallbackLabels);
      } else {
        setAvailableLabels([]);
      }
    }
  };

  const buildColumnsFromNormalizedData = (contacts, predefinedLabels = [], availableLabels = []) => {
    if (!contacts || contacts.length === 0) {
      return [];
    }

    // Get all unique keys from contacts
    const allKeys = new Set();
    contacts.forEach(contact => {
      Object.keys(contact).forEach(key => allKeys.add(key));
    });

    // Determine the email field name from the data (API might return 'email' or 'emailid')
    const emailField = allKeys.has('emailid') ? 'emailid' : (allKeys.has('email') ? 'email' : 'emailid');

    // Define core columns that should always appear first
    const coreColumns = [
      {
        title: 'Name',
        dataIndex: 'username',
        key: 'username',
        width: 160,
        render: (name, record) => (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Avatar icon={<UserOutlined />} size="small" style={{ marginRight: 8 }} />
            <div>
              <div style={{ fontWeight: 500 }}>{name || 'N/A'}</div>
              <div style={{ fontSize: 12, color: '#666' }}>
                {record.usercategory || record.userdata_category || 'General'}
              </div>
            </div>
          </div>
        ),
      },
      {
        title: 'Label',
        dataIndex: 'label',
        key: 'label',
        width: 160,
        render: (label) => {
          if (!label) return <span style={{ color: '#94a3b8', fontSize: 12 }}>N/A</span>;
          
          let labels = [];
          
          try {
            if (typeof label === 'string' && label.startsWith('[') && label.endsWith(']')) {
              labels = JSON.parse(label).filter(Boolean);
            } else if (typeof label === 'string' && label.includes(',')) {
              labels = label.split(',').map(l => l.trim()).filter(Boolean);
            } else if (typeof label === 'string') {
              labels = [label.trim()].filter(Boolean);
            } else if (Array.isArray(label)) {
              labels = label.filter(Boolean);
            }
          } catch (e) {
            labels = typeof label === 'string' ? [label.trim()].filter(Boolean) : [];
          }
          
          if (labels.length === 0) return <span style={{ color: '#94a3b8', fontSize: 12 }}>N/A</span>;
          
          const firstLabel = labels[0];
          const moreCount = labels.length > 1 ? labels.length - 1 : 0;
          
          // Build tooltip content for all labels
          const tooltipContent = (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {labels.map((item, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#f1f5f9', padding: '3px 6px', background: 'rgba(255,255,255,0.08)', borderRadius: 4 }}>
                  {item}
                </div>
              ))}
            </div>
          );
          
          return (
            <Tooltip title={tooltipContent} placement="top" overlayInnerStyle={{ background: '#1e293b', borderRadius: 6, padding: '8px 10px' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', cursor: moreCount > 0 ? 'pointer' : 'default' }}>
                <Tag 
                  size="small" 
                  style={{ 
                    fontSize: 12, lineHeight: '20px', height: 22, padding: '0 8px', borderRadius: 4, margin: 0,
                    maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    background: '#dcfce7', color: '#15803d', border: 'none', fontWeight: 500
                  }}
                >
                  {firstLabel}
                </Tag>
                {moreCount > 0 && <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginLeft: 5 }}>+{moreCount}</span>}
              </span>
            </Tooltip>
          );
        },
      },
      {
        title: 'Phone',
        dataIndex: 'mobileno',
        key: 'mobileno',
        width: 130,
        render: (phone) => (
          <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <PhoneOutlined style={{ marginRight: 4, color: '#0036b0' }} />
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={phone || 'N/A'}>
              {phone || 'N/A'}
            </span>
          </div>
        ),
      },
      {
        title: 'Email',
        dataIndex: emailField,  // Dynamically use emailid or email based on what API returns
        key: 'email_display',   // Unique key to avoid conflicts
        width: 200,
        render: (email) => email ? (
          <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <MailOutlined style={{ marginRight: 4, color: '#0036b0' }} />
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={email}>
              {email}
            </span>
          </div>
        ) : 'N/A',
      }
    ];

    // Fields already shown in core columns - exclude from dynamic columns to prevent duplicates
    const coreFieldsShown = new Set(['username', 'mobileno', 'emailid', 'email', 'label']);
    
    // Internal/system fields that should never be shown as columns
    const internalFields = new Set([
      'key', 'id', 'userid', 'userdata', 'companyid', 'createdon', 'updatedon', 'rowno'
    ]);
    
    // Combine both sets for exclusion from dynamic columns
    const excludeFields = new Set([...coreFieldsShown, ...internalFields]);

    // Generate dynamic columns for remaining fields
    const dynamicColumns = [];
    
    Array.from(allKeys)
      .filter(key => !excludeFields.has(key))
      .sort()
      .forEach(key => {
        // Skip if all values for this key are null/undefined/empty
        const hasValue = contacts.some(contact => {
          const value = contact[key];
          return value !== null && value !== undefined && value !== '';
        });
        
        if (!hasValue) return;

        const column = {
          title: key.replace(/userdata_/g, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          dataIndex: key,
          key: key,
          width: 120,
          render: (value) => {
            if (value === null || value === undefined || value === '') {
              return 'N/A';
            }
            
            // Handle array values (like labels)
            if (Array.isArray(value)) {
              return value.map((item, index) => (
                <Tag key={index} size="small">{item}</Tag>
              ));
            }
            
            // Handle string values that might be comma-separated
            if (typeof value === 'string' && (key.includes('label') || key.includes('tag'))) {
              const items = value.split(',').map(item => item.trim()).filter(Boolean);
              if (items.length > 1) {
                return items.map((item, index) => (
                  <Tag key={index} size="small">{item}</Tag>
                ));
              }
            }

            const text = String(value);
            return (
              <span
                title={text}
                style={{
                  display: 'inline-block',
                  maxWidth: '100%',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  verticalAlign: 'middle',
                }}
              >
                {text}
              </span>
            );
          },
        };

        dynamicColumns.push(column);
      });

    return [...coreColumns, ...dynamicColumns];
  };

  // Rate Calculation Functions
  const groupContactsByCountryCode = () => {
    const countryCodeCounts = {};
    
    // IMPORTANT: Use totalSelectedContacts which contains ALL selected contacts across all pages
    // This array is properly maintained when selecting/deselecting contacts on any page
    const selectedContacts = totalSelectedContacts;
    
    console.log('groupContactsByCountryCode - Total selected contacts:', selectedContacts.length);
    console.log('groupContactsByCountryCode - allSelectedContactIds size:', allSelectedContactIds.size);
    
    // If no contacts in totalSelectedContacts array, it means we might need to fetch them
    if (selectedContacts.length === 0 && allSelectedContactIds.size > 0) {
      console.warn('totalSelectedContacts is empty but allSelectedContactIds has items. This might indicate a data sync issue.');
      // Fallback: Try to get contacts from current page if available
      const fallbackContacts = (filteredContacts.length > 0 ? filteredContacts : contacts)
        .filter(contact => allSelectedContactIds.has(contact.userid || contact.id));
      console.log('Using fallback contacts from current page:', fallbackContacts.length);
      return groupContactsArray(fallbackContacts);
    }
    
    // Group by country code
    selectedContacts.forEach(contact => {
      const countryCode = contact.countrycode || '91'; // Default to +91 if not present
      const formattedCountryCode = countryCode.startsWith('+') ? countryCode : `+${countryCode}`;
      
      if (countryCodeCounts[formattedCountryCode]) {
        countryCodeCounts[formattedCountryCode]++;
      } else {
        countryCodeCounts[formattedCountryCode] = 1;
      }
    });
    
    // Convert to array format required by API
    const result = Object.entries(countryCodeCounts).map(([countrycode, count]) => ({
      countrycode,
      count: count.toString()
    }));
    
    console.log('groupContactsByCountryCode - Result:', result);
    return result;
  };
  
  // Helper function to group an array of contacts by country code
  const groupContactsArray = (contactsArray) => {
    const countryCodeCounts = {};
    
    contactsArray.forEach(contact => {
      const countryCode = contact.countrycode || '91';
      const formattedCountryCode = countryCode.startsWith('+') ? countryCode : `+${countryCode}`;
      
      if (countryCodeCounts[formattedCountryCode]) {
        countryCodeCounts[formattedCountryCode]++;
      } else {
        countryCodeCounts[formattedCountryCode] = 1;
      }
    });
    
    return Object.entries(countryCodeCounts).map(([countrycode, count]) => ({
      countrycode,
      count: count.toString()
    }));
  };

  const calculateEstimatedCost = async () => {
    try {
      setRateCalculationLoading(true);
      
      const contactsDetails = groupContactsByCountryCode();
      
      if (contactsDetails.length === 0) {
        message.warning('No contacts selected for rate calculation');
        return;
      }
      
      // Get selected template category
      const selectedTemplate = templates.find(t => t.templateid === campaignData.template.id);
      const templateCategory = (selectedTemplate?.templatecategory || 'marketing').toLowerCase();
      
      const payload = {
        category: templateCategory,
        contactsdetails: contactsDetails
      };
      
      const response = await axiosInstance.post('/whatsappcost/calculate', payload);
      
      if (response.data && response.data.status) {
        const responseData = response.data.entity;
        setRateDetails(responseData);
        
        // Set the final price from the response
        const finalPrice = responseData.finalPrice || 0;
        setEstimatedCost(finalPrice);
        
        // Store discount information for display
        if (responseData.discountPercentage && responseData.discountAmount) {
          setCampaignData(prev => ({
            ...prev,
            discountInfo: {
              discountid: responseData.discountid,
              discountPercentage: responseData.discountPercentage,
              discountAmount: responseData.discountAmount,
              discountedAmount: responseData.discountedAmount,
              originalAmount: finalPrice + responseData.discountAmount
            }
          }));
        }
        
        
      }
    } catch (error) {
      console.error('Error calculating estimated cost:', error);
      
      // Reset estimated cost and rate details when API fails
      setEstimatedCost(0);
      setRateDetails(null);
      
      // Clear any existing discount info
      setCampaignData(prev => ({
        ...prev,
        discountInfo: null
      }));
      
      // Show appropriate error message based on error type
      if (error.response?.status === 404) {
        message.error('Cost calculation service is currently unavailable');
      } else {
        message.error('Failed to calculate estimated cost. Please try again.');
      }
    } finally {
      setRateCalculationLoading(false);
    }
  };

  // Function to fetch discount details
  // Updated fetchDiscountDetails to open modal and fetch data
const fetchDiscountDetails = async () => {
  try {
    setDiscountLoading(true);
    setDiscountModalVisible(true);
    
    const payload = { page: 1, pageSize: 100, search: "" };
    const response = await axiosInstance.post('/discount/details', payload);
    
    if (response.data?.status && response.data.entity?.discounts) {
      setDiscountsData(response.data.entity.discounts);
      
      // Set default country to the first available
      const uniqueCodes = [...new Set(response.data.entity.discounts.map(d => d.countrycode))];
      if (uniqueCodes.length > 0) {
        setSelectedCountryCode(uniqueCodes[0]);
      }
      
      // Default to Utility or based on template category
      const templateCategory = templates.find(t => t.templateid === campaignData.template.id)?.templatecategory?.toLowerCase();
      setSelectedCategory(templateCategory === 'authentication' ? 'Authentication' : 'Utility');
    }
  } catch (error) {
    console.error('Error fetching discounts:', error);
    message.error('Failed to load discount details');
    setDiscountModalVisible(false);
  } finally {
    setDiscountLoading(false);
  }
};

  // API Functions
  const fetchContacts = async (filters = contactFilters, page = currentPage, size = pageSize, autoSelect = true) => {
    try {
      setContactsLoading(true);
      const payload = {
        companyid: currentUser?.companyid || '',
        userid: currentUser?.userid || '',
        page: page,
        limit: size,
        search: filters.search || '',
        mobileno: filters.mobileno || '',
        usercategory: filters.usercategory || '',
        state: filters.state || '',
        city: filters.city || ''
      };

      const response = await axiosInstance.post('/contact/details', payload);
      
      if (response.data && response.data.statuscode === 200) {
        const contactsData = response.data.entity.contacts || [];
        const mappedContacts = contactsData.map((contact, index) => ({
          ...contact,
          key: contact.userid,
          id: contact.userid,
          rowno: (page - 1) * size + index + 1
        }));
        
        setContacts(mappedContacts);
        setFilteredContacts(mappedContacts);
        setTotalContacts(response.data.entity.total || mappedContacts.length);
        
        // Auto-select contacts based on segment type and filters
        if (autoSelect) {
          if (campaignData.segment.type === 'all') {
            const contactIds = mappedContacts.map(contact => contact.userid);
            setSelectedRowKeys(contactIds);
            setAllContactsSelected(contactIds.length > 0);
            
            // Update total selected contacts for 'all' selections
            const newAllSelectedIds = new Set(contactIds);
            setAllSelectedContactIds(newAllSelectedIds);
            setTotalSelectedContacts(mappedContacts);
            
            setCampaignData(prev => ({
              ...prev,
              segment: {
                ...prev.segment,
                selectedContacts: mappedContacts
              }
            }));
          }
        } else {
          // When not auto-selecting, sync selectedRowKeys with current page contacts that are in total selected
          const currentPageSelectedIds = mappedContacts
            .filter(contact => allSelectedContactIds.has(contact.userid))
            .map(contact => contact.userid);
          setSelectedRowKeys(currentPageSelectedIds);
        }
      }
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
      message.error('Failed to load contacts');
    } finally {
      setContactsLoading(false);
      setSearchLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      setTemplatesLoading(true);
      const payload = {
        page: 1,
        pageSize: 100, // Get all templates for campaign builder
        templatename: templateSearchText || '',
        status: 'approved', // Only get approved templates for campaigns
        language: ''
      };

      const response = await axiosInstance.post('/template/details', payload);
      
      if (response.data && response.data.statuscode == 200) {
        const apiTemplates = response.data.entity.templates || [];
        // Filter only approved templates as an additional safety check
        const approvedTemplates = apiTemplates.filter(template => 
          template.status && template.status.toLowerCase() === 'approved'
        );
        
        const mappedTemplates = approvedTemplates.map(template => ({
          ...template,
          variables: extractVariablesFromTemplate(template.templatecontent || ''),
          content: template.templatecontent || ''
        }));
        
        setTemplates(mappedTemplates);
      } else {
        // message.error("Failed to fetch templates");
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      message.error('Failed to load templates');
    } finally {
      setTemplatesLoading(false);
    }
  };

  // Search functionality
  const debouncedSearch = useCallback((searchValue) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      handleSearch(searchValue);
    }, 500);
  }, []);

  const handleSearch = (searchValue = searchText) => {
    setSearchLoading(true);
    const newFilters = { ...contactFilters, search: searchValue };
    setContactFilters(newFilters);
    setCurrentPage(1);
    // Don't auto-select contacts when searching
    fetchContacts(newFilters, 1, pageSize, false);
  };

  const onSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    debouncedSearch(value);
  };

  // API Functions for States and Cities
  const fetchStates = async () => {
    try {
      setStatesLoading(true);
      const response = await axiosInstance.post('/statelist', {});

      if (response.data?.statuscode === 200) {
        setStatesList(response.data?.entity?.statelist || []);
      } else {
        message.error('Failed to fetch states');
        setStatesList([]);
      }
    } catch (error) {
      console.error('Fetch states error:', error);
      message.error('Failed to fetch states');
      setStatesList([]);
    } finally {
      setStatesLoading(false);
    }
  };

  const fetchCities = async (stateId) => {
    if (!stateId) {
      setCitiesList([]);
      return Promise.resolve();
    }

    try {
      setCitiesLoading(true);
      const response = await axiosInstance.post('/citylist', {
        stateId: stateId,
      });

      if (response.data?.statuscode === 200) {
        setCitiesList(response.data?.entity?.citylist || []);
        return Promise.resolve();
      } else {
        message.error('Failed to fetch cities');
        setCitiesList([]);
        return Promise.reject();
      }
    } catch (error) {
      console.error('Fetch cities error:', error);
      message.error('Failed to fetch cities');
      setCitiesList([]);
      return Promise.reject();
    } finally {
      setCitiesLoading(false);
    }
  };

  // Address Book Management Functions
  const fetchAddressBooks = async () => {
    try {
      setAddressBookLoading(true);
      const response = await axiosConfig.get('/addressbook/details');
      if (response.data?.statuscode === 200 && response.data?.entity) {
        const books = response.data.entity;
        setAddressBooks(books);
        
        // Set first address book as current if none selected
        if (books.length > 0 && !currentAddressBook) {
          setCurrentAddressBook(books[0]);
        }
      } else {
        setAddressBooks([]);
        setCurrentAddressBook(null);
        setContacts([]);
        setFilteredContacts([]);
      }
    } catch (error) {
      console.error('Error fetching address books:', error);
      setAddressBooks([]);
      setCurrentAddressBook(null);
      setContacts([]);
      setFilteredContacts([]);
    } finally {
      setAddressBookLoading(false);
    }
  };

  const handleAddressBookSelect = (addressBook) => {
    setCurrentAddressBook(addressBook);
    // Reset selections when switching address books
    setSelectedRowKeys([]);
    setAllSelectedContactIds(new Set());
    setTotalSelectedContacts([]);
    setCurrentPage(1);
    // fetchContacts will be called by useEffect when currentAddressBook changes
  };

  // Updated fetchContacts function to use address book API with dynamic filters
  const fetchContactsFromAddressBook = async (
    filters = { search: '', mobileno: '', labelfilter: [] },
    page = currentPage,
    size = pageSize
  ) => {
    // Don't fetch contacts if no address book is selected
    if (!currentAddressBook?.bookid) {
      setContacts([]);
      setFilteredContacts([]);
      setTotalContacts(0);
      setContactColumns([]); // Reset dynamic columns
      return;
    }

    try {
      setContactsLoading(true);

      const requestBody = {
        bookid: currentAddressBook.bookid,
        limit: size,
        offset: (page - 1) * size,
        searchby: filters.search || "",
        labelfilter: Array.isArray(filters.labelfilter) ? filters.labelfilter.join(',') : (filters.labelfilter || "")
      };

      // Add mobile filter if provided - Core Fix
      if (filters.mobileno) {
        requestBody.mobileno = filters.mobileno;
      }

      const response = await axiosConfig.post('/addressbook/users', requestBody);

      if ((response.data?.statuscode === 200 || response.data?.status === true) && response.data?.entity) {
        const entity = response.data.entity;
        // Handle the new API response structure where users are in entity.users
        const contactsData = entity.users || entity;
        const normalizedData = normalizeContactDataWithUserData(contactsData);
        
        // Map contacts with proper keys - API already filtered the data
        const mappedContacts = normalizedData.map((contact, index) => ({
          ...contact,
          key: contact.userid || contact.id || index,
          id: contact.userid || contact.id || index,
          rowno: (page - 1) * size + index + 1
        }));
        
        // Set contacts and filtered contacts to the same data (API handles filtering)
        setContacts(mappedContacts);
        setFilteredContacts(mappedContacts);
        setTotalContacts(entity.totalRecords || mappedContacts.length);
        
        // Fetch all labels from entire address book (not just current page)
        fetchAllLabelsFromAddressBook();
        
        // Generate dynamic columns based on the normalized data
        const dynamicColumns = buildColumnsFromNormalizedData(normalizedData, [], availableLabels);
        setContactColumns(dynamicColumns);
        
        // Sync selectedRowKeys with current page contacts that are in total selected
        const currentPageSelectedIds = mappedContacts
          .filter(contact => allSelectedContactIds.has(contact.userid || contact.id))
          .map(contact => contact.userid || contact.id);
        setSelectedRowKeys(currentPageSelectedIds);
      }
    } catch (error) {
      console.error('Failed to fetch contacts from address book:', error);
      message.error('Failed to load contacts');
      setContacts([]);
      setFilteredContacts([]);
      setTotalContacts(0);
      setContactColumns([]); // Reset dynamic columns on error
    } finally {
      setContactsLoading(false);
      setSearchLoading(false);
    }
  };

  // API-based filter handlers - Core Fix
  const handleNameFilterChange = (value) => {
    setNameFilter(value);
    // Debounce API call for name filter
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      applyDynamicFilters({ search: value, mobileno: mobileFilter, labelfilter: labelFilter });
    }, 500);
  };

  const handleMobileFilterChange = (value) => {
    setMobileFilter(value);
    // Debounce API call for mobile filter
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      applyDynamicFilters({ search: nameFilter, mobileno: value, labelfilter: labelFilter });
    }, 500);
  };

  const handleLabelFilterChange = (selectedLabels) => {
    setLabelFilter(selectedLabels);
    // Apply immediately for label changes (no debounce needed)
    applyDynamicFilters({ search: nameFilter, mobileno: mobileFilter, labelfilter: selectedLabels });
  };

  // API-based dynamic filtering - Core Fix
  const applyDynamicFilters = async (filters = { search: '', mobileno: '', labelfilter: [] }) => {
    if (!currentAddressBook?.bookid) {
      setFilteredContacts([]);
      return;
    }

    try {
      setContactsLoading(true);

      const requestBody = {
        bookid: currentAddressBook.bookid,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        searchby: filters.search || '',
        labelfilter: Array.isArray(filters.labelfilter) 
          ? filters.labelfilter.join(',') 
          : (filters.labelfilter || '')
      };

      // Add mobile filter if provided
      if (filters.mobileno) {
        requestBody.mobileno = filters.mobileno;
      }

      const response = await axiosConfig.post('/addressbook/users', requestBody);

      if ((response.data?.statuscode === 200 || response.data?.status === true) && response.data?.entity) {
        const entity = response.data.entity;
        const contactsData = entity.users || entity;
        const normalizedData = normalizeContactDataWithUserData(contactsData);
        
        // Update total from API
        const totalRecordsFromAPI = entity.totalRecords || 0;
        setTotalContacts(totalRecordsFromAPI);

        // Build columns dynamically
        const newColumns = buildColumnsFromNormalizedData(normalizedData, predefinedLabels, availableLabels);
        setContactColumns(newColumns);

        // Update filtered contacts with API results
        setFilteredContacts(normalizedData);
        setContacts(normalizedData); // Also update base contacts

        // Reset pagination to first page on filter change
        setCurrentPage(1);
      } else {
        setFilteredContacts([]);
        setContacts([]);
        setTotalContacts(0);
        message.warning(response.data?.message || 'No contacts found with applied filters');
      }
    } catch (error) {
      console.error('Error applying filters:', error);
      message.error('Failed to apply filters. Please try again.');
      setFilteredContacts([]);
    } finally {
      setContactsLoading(false);
    }
  };

  // Clear all filters and reload original data - API-based
  const clearAllFilters = async () => {
    setNameFilter('');
    setMobileFilter('');
    setLabelFilter([]);
    setCurrentPage(1);
    setSelectedRowKeys([]);
    setAllSelectedContactIds(new Set());
    setTotalSelectedContacts([]);
    
    // Reload contacts without filters
    await applyDynamicFilters({ search: '', mobileno: '', labelfilter: [] });
  };

  const handleLabelChange = (selectedLabels) => {
    setSelectedLabels(selectedLabels);
    // Reset pagination and contact selection
    setCurrentPage(1);
    setSelectedRowKeys([]);
    setAllSelectedContactIds(new Set());
    setTotalSelectedContacts([]);
    
    // Fetch contacts with new label filter
    if (currentAddressBook) {
      fetchContactsFromAddressBook({ 
        search: searchText, 
        labelfilter: selectedLabels 
      }, 1, pageSize);
    }
  };

  // Extract variables from template content
  const extractVariablesFromTemplate = (content) => {
    const regex = /\{\{(\d+)\}\}/g;
    const variables = [];
    let match;
    
    while ((match = regex.exec(content)) !== null) {
      if (!variables.includes(match[0])) {
        variables.push(match[0]);
      }
    }
    
    return variables.sort((a, b) => {
      const numA = parseInt(a.replace(/[{}]/g, ''));
      const numB = parseInt(b.replace(/[{}]/g, ''));
      return numA - numB;
    });
  };

  // Filter templates based on search text
  const filteredTemplates = useMemo(() => {
    if (!templateSearchText) return templates;
    
    return templates.filter(template =>
      template.templatename?.toLowerCase().includes(templateSearchText.toLowerCase()) ||
      template.templatecategory?.toLowerCase().includes(templateSearchText.toLowerCase()) ||
      template.templatelanguage?.toLowerCase().includes(templateSearchText.toLowerCase()) ||
      (template.content && template.content.toLowerCase().includes(templateSearchText.toLowerCase()))
    );
  }, [templates, templateSearchText]);

  // Handle selecting all contacts across all pages
  const handleSelectAllContacts = async (autoSelect = false) => {
    console.log('handleSelectAllContacts called, allSelectedContactIds.size:', allSelectedContactIds.size, 'autoSelect:', autoSelect);
    
    // If contacts are already selected and this is not an auto-select, deselect all
    if (allSelectedContactIds.size > 0 && !autoSelect) {
      console.log('Deselecting all contacts...');
      // Deselect all - no need for loading state for deselection
      setSelectedRowKeys([]);
      setAllContactsSelected(false);
      setAllSelectedContactIds(new Set());
      setTotalSelectedContacts([]);
      setCampaignData(prev => ({
        ...prev,
        segment: {
          ...prev.segment,
          type: 'custom', // Switch to custom when manually deselecting
          selectedContacts: []
        }
      }));
      setSelectAllLoading(false); // Ensure loading is reset
      // message.success('All contacts deselected');
      return; // Exit early for deselection
    }
    
    console.log('Selecting all contacts...');
    // Select all contacts - show loading
    setSelectAllLoading(true);
    try {
        // Use address book API to get all contacts
        if (!currentAddressBook?.bookid) {
          message.warning('Please select an address book first');
          return;
        }

        const requestBody = {
          bookid: currentAddressBook.bookid,
          limit: 10000, // Get all contacts from address book
          offset: 0,
          searchby: searchText || "",
          labelfilter: Array.isArray(selectedLabels) ? selectedLabels.join(',') : (selectedLabels || "")
        };

        const response = await axiosConfig.post('/addressbook/users', requestBody);
        
        if ((response.data?.statuscode === 200 || response.data?.status === true) && response.data?.entity) {
          const entity = response.data.entity;
          const contactsData = entity.users || entity;
          const normalizedData = normalizeContactDataWithUserData(contactsData);
          
          // Apply local filters if any
          let filteredData = normalizedData;
          
          // Apply name filter
          if (nameFilter && nameFilter.trim()) {
            const searchTerm = nameFilter.toLowerCase();
            filteredData = filteredData.filter(contact =>
              contact.username && contact.username.toLowerCase().includes(searchTerm)
            );
          }
          
          // Apply mobile filter
          if (mobileFilter && mobileFilter.trim()) {
            filteredData = filteredData.filter(contact =>
              contact.mobileno && contact.mobileno.includes(mobileFilter)
            );
          }
          
          // Apply label filter
          if (labelFilter && labelFilter.length > 0) {
            const selected = labelFilter.map(l => l.toLowerCase());
            filteredData = filteredData.filter(contact => {
              const contactLabels = [];
              const labelFields = ['label', 'labels', 'userdata_labels', 'userlabels'];
              labelFields.forEach(field => {
                if (contact[field]) {
                  const val = contact[field];
                  if (typeof val === 'string') {
                    const s = val.trim();
                    let parsed = null;
                    if (s.startsWith('[') && s.endsWith(']')) {
                      try { parsed = JSON.parse(s); } catch (e) { parsed = null; }
                    }
                    if (Array.isArray(parsed)) {
                      contactLabels.push(...parsed);
                    } else {
                      contactLabels.push(...s.split(',').map(label => label.trim()).filter(Boolean));
                    }
                  } else if (Array.isArray(val)) {
                    contactLabels.push(...val);
                  }
                }
              });
              if (contactLabels.length === 0) return false;
              const lowerContactLabels = contactLabels.map(l => String(l).toLowerCase());
              return selected.every(sel => lowerContactLabels.includes(sel));
            });
          }
          
          const allMappedContacts = filteredData.map((contact, index) => ({
            ...contact,
            key: contact.userid || contact.id || index,
            id: contact.userid || contact.id || index,
            rowno: index + 1
          }));
          
          const currentPageIds = filteredContacts.map(contact => contact.userid || contact.id);
          
          // Select all contacts without any limit
          const finalIds = allMappedContacts.map(c => c.userid || c.id);
          setSelectedRowKeys(currentPageIds.filter(id => finalIds.includes(id))); // UI only for current page
          setAllSelectedContactIds(new Set(finalIds));
          setTotalSelectedContacts(allMappedContacts);
          setAllContactsSelected(true);
          setCampaignData(prev => ({
            ...prev,
            segment: {
              ...prev.segment,
              type: 'all',
              selectedContacts: allMappedContacts
            }
          }));

          // message.success(`${allMappedContacts.length} contacts selected`);
        } else {
          message.error('Failed to fetch contacts for selection');
        }
    } catch (error) {
      console.error('Error in handleSelectAllContacts:', error);
      message.error('Failed to select contacts');
    } finally {
      setSelectAllLoading(false);
    }
  };

  // Handle template preview
  const handleTemplatePreview = (template) => {
    
    setSelectedTemplatePreview(template);
    setTemplatePreviewVisible(true);
  };

  // Handle contact filters
  const handleContactFilters = async (newFilters) => {
    // Reset selection when filters change
    setSelectedRowKeys([]);
    setAllContactsSelected(false);
    setAllSelectedContactIds(new Set());
    setTotalSelectedContacts([]);
    setCampaignData(prev => ({
      ...prev,
      segment: {
        ...prev.segment,
        selectedContacts: []
      }
    }));
    
    // Update filters and fetch contacts
    setContactFilters(newFilters);
    await fetchContacts(newFilters, 1, pageSize);
    setCurrentPage(1);
  };

  // Step 1: Campaign Info
  const renderCampaignInfo = () => (
    <div className="broadcast-content-card">
      <div className="broadcast-section-header">
        <Title level={5} className="broadcast-section-title">Campaign Information</Title>
        <Text className="broadcast-section-desc">Give your campaign a name and optional description.</Text>
      </div>
      <Form layout="vertical" form={form}>
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <Form.Item
              label="Campaign Name"
              name="campaignName"
              rules={[{ required: true, message: 'Please enter campaign name' }]}
              validateStatus={!campaignData.name?.trim() && validationTriggered ? 'error' : ''}
              help={!campaignData.name?.trim() && validationTriggered ? 'Campaign name is required' : ''}
            >
              <Input
                placeholder="Enter campaign name (e.g., holiday_sale_2024)"
                value={campaignData.name}
                onChange={(e) => setCampaignData(prev => ({ ...prev, name: e.target.value }))}
                maxLength={100}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {campaignData.name?.length || 0}/100
              </Text>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <Form.Item
              label="Description (Optional)"
              name="description"
            >
              <TextArea
                placeholder="Brief description of your campaign"
                value={campaignData.description}
                onChange={(e) => setCampaignData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                maxLength={500}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {campaignData.description?.length || 0}/500
              </Text>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </div>
  );

  // Filter Modal Component
  const renderFilterModal = () => (
    <Modal
      title={
        <Space>
          <FilterOutlined />
          <Text strong>Filter Contacts</Text>
        </Space>
      }
      open={filterModalVisible}
      onCancel={() => setFilterModalVisible(false)}
      footer={[
        <Button key="clear" onClick={clearAllFilters}>
          Clear All Filters
        </Button>,
        <Button key="cancel" onClick={() => setFilterModalVisible(false)}>
          Cancel
        </Button>,
        <Button 
          key="apply" 
          type="primary" 
          onClick={() => setFilterModalVisible(false)}
        >
          Apply Filters
        </Button>
      ]}
      width="90%"
      style={{ maxWidth: 600 }}
    >
      <Row gutter={[16, 16]}>
        {/* Name Filter */}
        <Col span={24}>
          <Text strong>Filter by Name</Text>
          <Input
            placeholder="Enter name to filter"
            prefix={<UserOutlined />}
            value={nameFilter}
            onChange={(e) => handleNameFilterChange(e.target.value)}
            style={{ width: '100%', marginTop: 8 }}
            allowClear
          />
        </Col>

        {/* Mobile Filter */}
        <Col span={24}>
          <Text strong>Filter by Mobile</Text>
          <Input
            placeholder="Enter mobile number"
            prefix={<PhoneOutlined />}
            value={mobileFilter}
            onChange={(e) => handleMobileFilterChange(e.target.value)}
            style={{ width: '100%', marginTop: 8 }}
            allowClear
          />
        </Col>

        {/* Label Filter */}
        <Col span={24}>
          <Text strong>Filter by Labels</Text>
          <Select
            mode="multiple"
            placeholder="Select labels to filter"
            value={labelFilter}
            onChange={handleLabelFilterChange}
            style={{ width: '100%', marginTop: 8 }}
            allowClear
            maxTagCount="responsive"
          >
            {/* Predefined Labels */}
            {predefinedLabels.map(label => (
              <Option key={label.value} value={label.value} label={label.label}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: '#64748b', fontSize: 14 }}>{label.icon}</span>
                  <span style={{ color: '#191C1E', fontSize: 13 }}>{label.label}</span>
                </div>
              </Option>
            ))}
            
            {/* Available Labels from Contacts */}
            {availableLabels
              .filter(label => !predefinedLabels.some(predefined => predefined.value === label.toLowerCase()))
              .map(label => (
                <Option key={label} value={label} label={label}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <TagOutlined style={{ color: '#64748b', fontSize: 14 }} />
                    <span style={{ color: '#191C1E', fontSize: 13 }}>{label}</span>
                  </div>
                </Option>
              ))
            }
          </Select>
        </Col>

        {/* Active Filters Display */}
        {(nameFilter || mobileFilter || labelFilter.length > 0) && (
          <Col span={24}>
            <Divider />
            <Space wrap>
              <Text strong>Active Filters:</Text>
              
              {nameFilter && (
                <Tag 
                  closable 
                  onClose={() => handleNameFilterChange('')}
                  color="blue"
                >
                  Name: {nameFilter}
                </Tag>
              )}
              
              {mobileFilter && (
                <Tag 
                  closable 
                  onClose={() => handleMobileFilterChange('')}
                  color="green"
                >
                  Mobile: {mobileFilter}
                </Tag>
              )}
              
              {labelFilter.map(label => {
                const predefinedLabel = predefinedLabels.find(p => p.value === label);
                return (
                  <Tag
                    key={label}
                    closable
                    onClose={() => handleLabelFilterChange(labelFilter.filter(l => l !== label))}
                    color={predefinedLabel?.color || 'default'}
                    icon={predefinedLabel?.icon}
                  >
                    {predefinedLabel?.label || label}
                  </Tag>
                );
              })}
            </Space>
          </Col>
        )}
      </Row>
    </Modal>
  );

  // Step 2: Contact Segmentation
  // Address Book Contact Selection Section
  const renderContactSelection = () => {
    // Use dynamic contactColumns from state (generated by buildColumnsFromNormalizedData)

    const rowSelection = {
      selectedRowKeys,
      onChange: (keys, rows) => {
        const currentPageContactIds = filteredContacts.map(contact => contact.userid || contact.id);

        // Update selectedRowKeys for UI
        setSelectedRowKeys(keys);

        // Update authoritative set: remove current page ids then add selected keys
        const newAllSelectedIds = new Set(allSelectedContactIds);
        currentPageContactIds.forEach(id => newAllSelectedIds.delete(id));
        keys.forEach(id => newAllSelectedIds.add(id));

        // Update the selected contacts list with all selected contacts (merge)
        const prevMap = new Map(totalSelectedContacts.map(c => [(c.userid || c.id), c]));
        (rows || []).forEach(r => {
          const id = r.userid || r.id;
          if (keys.includes(id)) prevMap.set(id, r);
          else prevMap.delete(id);
        });
        // Also remove any deselections on current page
        currentPageContactIds.forEach(id => {
          if (!keys.includes(id) && prevMap.has(id)) prevMap.delete(id);
        });

        const updatedTotalSelected = Array.from(prevMap.values());

        setAllSelectedContactIds(newAllSelectedIds);
        setTotalSelectedContacts(updatedTotalSelected);

        // When user manually selects/deselects contacts, switch to custom segment type
        setCampaignData(prev => ({
          ...prev,
          segment: {
            ...prev.segment,
            type: 'custom',
            selectedContacts: updatedTotalSelected
          }
        }));

        // Update allContactsSelected flag
        setAllContactsSelected(newAllSelectedIds.size === totalContacts && totalContacts > 0);
      },
      getCheckboxProps: (record) => ({
        disabled: false
      })
    };

    return (
      <div className="broadcast-content-card">
        <div className="broadcast-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
          <div>
            <Title level={5} className="broadcast-section-title">Select Contacts</Title>
            <Text className="broadcast-section-desc">Choose the contacts you want to send this campaign to.</Text>
          </div>
          <Tag color={allSelectedContactIds.size === totalContacts && totalContacts > 0 ? 'green' : 'blue'} style={{ margin: 0, fontWeight: 600, fontSize: 13, padding: '2px 10px', borderRadius: 12 }}>
            {allSelectedContactIds.size} / {totalContacts} selected
          </Tag>
        </div>
        {/* Validation Alert */}
        {allSelectedContactIds.size === 0 && validationTriggered && (
          <Alert
            message="Please select at least one contact to continue."
            type="error"
            showIcon
            style={{ marginBottom: 12, borderRadius: 8 }}
            banner
          />
        )}
        
        {/* Unified Toolbar */}
        <div className="bc-contacts-toolbar">
          <div className="bc-contacts-toolbar__row">
            <div className="bc-contacts-toolbar__field">
              <label className="bc-contacts-toolbar__label">Address Book</label>
              {addressBookLoading ? (
                <div style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: 6,
                  padding: '8px 11px',
                  background: '#fafafa',
                  height: 36
                }}>
                  <div style={{
                    height: 20,
                    background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                    borderRadius: 4,
                    animation: skeletonAnimation
                  }} />
                </div>
              ) : (
                <Select
                  value={currentAddressBook?.bookid}
                  onChange={(bookId) => {
                    const selectedBook = addressBooks.find(book => book.bookid === bookId);
                    handleAddressBookSelect(selectedBook);
                  }}
                  placeholder="Select an address book"
                  style={{ width: '100%' }}
                  disabled={addressBookLoading}
                  size="middle"
                >
                  {addressBooks.map(book => (
                    <Option key={book.bookid} value={book.bookid}>
                      <Space>
                        <BookOutlined />
                        {book.bookname}
                        <Text type="secondary">({book.totaluser || 0})</Text>
                      </Space>
                    </Option>
                  ))}
                </Select>
              )}
            </div>

            {currentAddressBook && (
              <div className="bc-contacts-toolbar__field" style={{ flex: '1 1 auto' }}>
                <label className="bc-contacts-toolbar__label">Search</label>
                <Input
                  placeholder="Search by name, phone..."
                  prefix={<SearchOutlined style={{ color: '#bbb' }} />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  allowClear
                  size="middle"
                />
              </div>
            )}

            {currentAddressBook && (
              <div className="bc-contacts-toolbar__actions">
                <Button 
                  icon={<FilterOutlined />}
                  onClick={() => setFilterModalVisible(true)}
                  type={nameFilter || mobileFilter || labelFilter.length > 0 ? 'primary' : 'default'}
                  size="middle"
                >
                  {(nameFilter || mobileFilter || labelFilter.length > 0) 
                    ? `Filters (${[nameFilter, mobileFilter, ...labelFilter].filter(Boolean).length})` 
                    : 'Filter'}
                </Button>
                {contactsLoading ? (
                  <div style={{
                    display: 'inline-block', width: 36, height: 36,
                    background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                    borderRadius: 6, animation: skeletonAnimation
                  }} />
                ) : (
                  <Tooltip title="Refresh contacts">
                    <Button 
                      icon={<ReloadOutlined />} 
                      onClick={() => fetchContactsFromAddressBook({ 
                        search: searchText, 
                        labelfilter: selectedLabels 
                      }, currentPage, pageSize)}
                      size="middle"
                    />
                  </Tooltip>
                )}
                <Button 
                  type="primary" 
                  icon={<TeamOutlined />}
                  onClick={() => {
                    console.log('Button clicked! Current selection size:', allSelectedContactIds.size);
                    handleSelectAllContacts();
                  }}
                  disabled={totalContacts === 0}
                  loading={selectAllLoading}
                  size="middle"
                >
                  {allSelectedContactIds.size > 0 ? `Deselect All (${allSelectedContactIds.size})` : `Select All (${totalContacts})`}
                </Button>
              </div>
            )}
          </div>

          {/* Active Filters Chips */}
          {currentAddressBook && (nameFilter || mobileFilter || labelFilter.length > 0) && (
            <div className="bc-contacts-toolbar__filters">
              <Space wrap size={[6, 6]}>
                {nameFilter && (
                  <Tag closable onClose={() => handleNameFilterChange('')} color="blue" size="small">
                    Name: {nameFilter}
                  </Tag>
                )}
                {mobileFilter && (
                  <Tag closable onClose={() => handleMobileFilterChange('')} color="green" size="small">
                    Mobile: {mobileFilter}
                  </Tag>
                )}
                {labelFilter.map(label => {
                  const predefinedLabel = predefinedLabels.find(p => p.value === label);
                  return (
                    <Tag
                      key={label}
                      closable
                      onClose={() => handleLabelFilterChange(labelFilter.filter(l => l !== label))}
                      color={predefinedLabel?.color || 'default'}
                      size="small"
                    >
                      {predefinedLabel?.label || label}
                    </Tag>
                  );
                })}
                <Button type="text" size="small" onClick={clearAllFilters} icon={<CloseOutlined />} danger style={{ fontSize: 11, height: 22 }}>
                  Clear all
                </Button>
              </Space>
            </div>
          )}
        </div>


          {/* Contacts Table */}
          {currentAddressBook && (
              <div>
                {contactsLoading ? (
                  <ContactsSkeleton />
                ) : (
                  <Table
                    columns={contactColumns}
                    dataSource={filteredContacts}
                    rowKey={(record) => record.userid || record.id}
                    rowSelection={rowSelection}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalContacts,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} contacts (${allSelectedContactIds.size} selected)`,
                    onChange: (page, size) => {
                      setCurrentPage(page);
                      setPageSize(size);
                      fetchContactsFromAddressBook({ search: searchText, labelfilter: selectedLabels }, page, size);
                      // Scroll to top of the contact selection card
                      const cardElement = document.querySelector('.ant-card');
                      if (cardElement) {
                        cardElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }
                  }}
                  scroll={{ x: 800 }}
                  size="small"
                  locale={{
                    emptyText: currentAddressBook ? 
                      <Empty description="No contacts found in this address book" /> :
                      <Empty description="Please select an address book to view contacts" />
                  }}
                />
                )}
              </div>
          )}

          {/* No Address Book Selected */}
          {!currentAddressBook && !addressBookLoading && (
              <Empty 
                description="Please select an address book to view and select contacts"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
          )}
      </div>
    );
  };



  // Step 3: Template Selection
  const renderTemplateSelection = () => (
    <div className="broadcast-content-card">
      <div className="broadcast-section-header">
        <Title level={5} className="broadcast-section-title">Choose Template</Title>
        <Text className="broadcast-section-desc">Pick an approved WhatsApp message template for your campaign.</Text>
      </div>
      {/* Validation Alert */}
      {!campaignData.template.id && validationTriggered && (
        <Alert
          message="No Template Selected"
          description="Please select a template to continue with the campaign."
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      
      {/* Template Search */}
      <div className="bc-template-toolbar">
        <Input
          placeholder="Search templates by name..."
          prefix={<SearchOutlined style={{ color: '#bbb' }} />}
          value={templateSearchText}
          onChange={(e) => setTemplateSearchText(e.target.value)}
          allowClear
          size="middle"
          className="bc-template-toolbar__search"
        />
        <Tooltip title="Reload templates">
          <Button 
            icon={<ReloadOutlined />} 
            onClick={() => fetchTemplates()}
            loading={templatesLoading}
            size="middle"
          />
        </Tooltip>
      </div>

      {templatesLoading ? (
        <TemplateSkeleton />
      ) : (
        <>
          {/* Desktop Table */}
          <div className="bc-desktop-only">
            <Table
              columns={[
                {
                  title: '#',
                  dataIndex: 'index',
                  key: 'index',
                  width: 50,
                  render: (text, record, index) => index + 1,
                },
                {
                  title: 'Action',
                  key: 'action',
                  width: 90,
                  render: (text, record) => (
                    <Button
                      type={campaignData.template.id === record.templateid ? "primary" : "default"}
                      size="small"
                      onClick={() => setCampaignData(prev => ({
                        ...prev,
                        template: {
                          ...prev.template,
                          id: record.templateid,
                          name: record.templatename,
                          content: record.templatecontent || record.content || '',
                          variables: record.variables,
                          type: 'existing'
                        }
                      }))}
                      style={{
                        backgroundColor: campaignData.template.id === record.templateid ? '#0036b0' : undefined,
                        borderColor: campaignData.template.id === record.templateid ? '#0036b0' : undefined
                      }}
                    >
                      {campaignData.template.id === record.templateid ? '✓' : 'Select'}
                    </Button>
                  ),
                },
                {
                  title: 'Template',
                  dataIndex: 'templatename',
                  key: 'templatename',
                  render: (text, record) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FileTextOutlined style={{ color: '#0036b0' }} />
                      <div>
                        <div 
                          style={{ 
                            fontWeight: 500, 
                            cursor: 'pointer', 
                            color: '#0036b0',
                            textDecoration: 'underline'
                          }}
                          onClick={() => handleTemplatePreview(record)}
                        >
                          {text}
                        </div>
                        <div style={{ 
                          fontSize: '12px', 
                          color: '#666', 
                          marginTop: '2px',
                          maxWidth: '300px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {record.templatecontent || record.content || 'No content available'}
                        </div>
                      </div>
                    </div>
                  ),
                },
                {
                  title: 'Language',
                  dataIndex: 'templatelanguage',
                  key: 'templatelanguage',
                  width: 100,
                  render: (text) => text || 'en',
                },
                {
                  title: 'Category',
                  dataIndex: 'templatecategory',
                  key: 'templatecategory',
                  width: 120,
                  render: (text) => (
                    <Tag color="blue">{(text || 'MARKETING').toUpperCase()}</Tag>
                  ),
                },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  key: 'status',
                  width: 100,
                  render: (status) => {
                    const statusConfig = {
                      APPROVED: { color: 'green', icon: <CheckCircleOutlined />, text: 'Approved' },
                      PENDING: { color: 'orange', icon: <ClockCircleOutlined />, text: 'Pending' },
                      REJECTED: { color: 'red', icon: <ExclamationCircleOutlined />, text: 'Rejected' },
                    };
                    const statusUpper = (status || '').toUpperCase();
                    const config = statusConfig[statusUpper] || statusConfig.PENDING;
                    return (
                      <Tag color={config.color} icon={config.icon}>
                        {config.text}
                      </Tag>
                    );
                  },
                },
                {
                  title: 'Variables',
                  dataIndex: 'variables',
                  key: 'variables',
                  width: 100,
                  render: (variables) => variables ? variables.length : 0,
                },
              ]}
              dataSource={filteredTemplates}
              rowKey="templateid"
              scroll={{ x: 700 }}
              size="small"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} templates`,
              }}
              locale={{
                emptyText: templateSearchText ? "No approved templates found matching your search" : "No approved templates available"
              }}
            />
          </div>

          {/* Mobile Template Cards */}
          <div className="bc-mobile-only">
            {filteredTemplates.length === 0 ? (
              <Empty description={templateSearchText ? "No approved templates found matching your search" : "No approved templates available"} />
            ) : (
              <div className="bc-template-cards">
                {filteredTemplates.map((record) => {
                  const isSelected = campaignData.template.id === record.templateid;
                  const statusConfig = {
                    APPROVED: { color: 'green', icon: <CheckCircleOutlined />, text: 'Approved' },
                    PENDING: { color: 'orange', icon: <ClockCircleOutlined />, text: 'Pending' },
                    REJECTED: { color: 'red', icon: <ExclamationCircleOutlined />, text: 'Rejected' },
                  };
                  const statusUpper = (record.status || '').toUpperCase();
                  const config = statusConfig[statusUpper] || statusConfig.PENDING;
                  return (
                    <div
                      key={record.templateid}
                      className={`bc-template-card${isSelected ? ' bc-template-card--selected' : ''}`}
                      onClick={() => setCampaignData(prev => ({
                        ...prev,
                        template: {
                          ...prev.template,
                          id: record.templateid,
                          name: record.templatename,
                          content: record.templatecontent || record.content || '',
                          variables: record.variables,
                          type: 'existing'
                        }
                      }))}
                    >
                      <div className="bc-template-card__top">
                        <div className="bc-template-card__info">
                          <div className="bc-template-card__name">
                            <FileTextOutlined style={{ color: isSelected ? '#fff' : '#0036b0', fontSize: 14, flexShrink: 0 }} />
                            <span>{record.templatename}</span>
                          </div>
                          <div className="bc-template-card__preview">
                            {record.templatecontent || record.content || 'No content available'}
                          </div>
                        </div>
                        {isSelected && (
                          <div className="bc-template-card__check">
                            <CheckCircleOutlined />
                          </div>
                        )}
                      </div>
                      <div className="bc-template-card__bottom">
                        <div className="bc-template-card__tags">
                          <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>{(record.templatecategory || 'MARKETING').toUpperCase()}</Tag>
                          <Tag color={config.color} icon={config.icon} style={{ margin: 0, fontSize: 10 }}>{config.text}</Tag>
                          <span style={{ fontSize: 10, color: '#888' }}>{record.templatelanguage || 'en'}</span>
                        </div>
                        <span
                          className="bc-template-card__preview-link"
                          onClick={(e) => { e.stopPropagation(); handleTemplatePreview(record); }}
                        >
                          Preview
                        </span>
                      </div>
                    </div>
                  );
                })}
                <div style={{ textAlign: 'center', padding: '8px 0', color: '#888', fontSize: 12 }}>
                  Showing {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  // Template Variable Extraction and Mapping Functions

  // Returns 'document' | 'image' | 'video' when the template has a media header, null otherwise
  const getTemplateHeaderType = (tmpl) => {
    if (!tmpl) return null;
    const header = (tmpl.component || []).find(c => c.type === 'HEADER');
    if (header) {
      const fmt = (header.format || '').toUpperCase();
      if (['IMAGE', 'DOCUMENT', 'VIDEO'].includes(fmt)) return fmt.toLowerCase();
    }
    const direct = (tmpl.headertype || tmpl.header_type || tmpl.headerformat || tmpl.header_format || '').toUpperCase();
    if (['IMAGE', 'DOCUMENT', 'VIDEO'].includes(direct)) return direct.toLowerCase();
    return null;
  };

  const extractTemplateVariables = (template) => {
    if (!template) return [];
    
    const content = template.templatecontent || template.content || '';
    const variables = [];
    
    // Extract both {{1}}, {{2}} format and {{name}}, {{xyz}} format
    const variableRegex = /\{\{([^}]+)\}\}/g;
    let match;
    
    while ((match = variableRegex.exec(content)) !== null) {
      const variable = `{{${match[1]}}}`;
      if (!variables.includes(variable)) {
        variables.push(variable);
      }
    }
    
    return variables;
  };

  const determineVariableType = (variables) => {
    if (variables.length === 0) return 'number';
    
    // Check if all variables are numeric ({{1}}, {{2}}, etc.)
    const isNumeric = variables.every(variable => {
      const match = variable.match(/\{\{(\d+)\}\}/);
      return match !== null;
    });
    
    return isNumeric ? 'number' : 'name';
  };

  const handleVariableMapping = (variable, columnName) => {
    setTemplateMapping(prev => ({
      ...prev,
      mapping: {
        ...prev.mapping,
        [variable]: columnName
      }
    }));
  };

  const getPreviewContentWithMapping = (template) => {
    let content = template?.templatecontent || template?.content || '';
    
    // If we have address book data and mapping, use actual values
    if (currentAddressBook && contactColumns.length > 0 && contacts.length > 0) {
      const sampleContact = contacts[0]; // Use first contact as sample
      
      Object.entries(templateMapping.mapping).forEach(([variable, columnName]) => {
        let columnValue;
        
        // Handle userdata fields with dot notation (e.g., userdata.blood_group)
        if (columnName.startsWith('userdata.')) {
          const fieldName = columnName.replace('userdata.', '');
          
          // Try to get value from userdata object or flattened userdata_field
          if (sampleContact.userdata && typeof sampleContact.userdata === 'object') {
            columnValue = sampleContact.userdata[fieldName];
          } else if (sampleContact[`userdata_${fieldName}`]) {
            columnValue = sampleContact[`userdata_${fieldName}`];
          } else {
            columnValue = `[${columnName}]`;
          }
        } else {
          // Handle regular fields
          columnValue = sampleContact[columnName] || `[${columnName}]`;
        }
        
        content = content.replace(new RegExp(variable.replace(/[{}]/g, '\\$&'), 'g'), columnValue);
      });
    } else {
      // Fallback to variable samples if no mapping available
      if (template?.variables && template.variables.length > 0) {
        template.variables.forEach(variable => {
          const sample = campaignData.template.variableSamples[variable];
          if (sample) {
            content = content.replace(new RegExp(variable.replace(/[{}]/g, '\\$&'), 'g'), sample);
          }
        });
      }
    }
    
    return content;
  };

  // Step 4: Variable Examples & Preview
  const renderVariablesAndPreview = () => {
    const selectedTemplate = templates.find(t => t.templateid === campaignData.template.id);
    
    if (!selectedTemplate) {
      return (
        <div className="broadcast-content-card">
          <div className="broadcast-section-header">
            <Title level={5} className="broadcast-section-title">Variables & Preview</Title>
            <Text className="broadcast-section-desc">Map template variables and preview your message.</Text>
          </div>
          <Empty description="Please select a template first" />
        </div>
      );
    }

    // Extract variables from the selected template
    const templateVariables = extractTemplateVariables(selectedTemplate);
    const variableType = determineVariableType(templateVariables);

    // Build available columns for variable mapping from actual contact data
    // This ensures we only show fields that actually exist in the data
    const availableColumns = [];
    
    if (contacts.length > 0) {
      const sampleContact = contacts[0];
      
      // Internal/system fields that should NOT be available for mapping
      const internalFields = new Set([
        'key', 'id', 'userid', 'userdata', 'companyid', 'createdon', 'updatedon', 'rowno'
      ]);
      
      // Fields used for filtering/UI purposes only, not for variable mapping
      const filterOnlyFields = new Set(['label']);
      
      // Combine fields to exclude from mapping
      const excludeFromMapping = new Set([...internalFields, ...filterOnlyFields]);
      
      // Track which fields we've already added (to handle email/emailid aliasing)
      const addedFields = new Set();
      
      // Process all contact fields
      Object.keys(sampleContact).forEach(key => {
        // Skip excluded fields
        if (excludeFromMapping.has(key)) return;
        
        // Skip userdata_ prefixed fields (we'll handle userdata object separately)
        if (key.startsWith('userdata_')) return;
        
        // Skip userdata object itself (we'll process its contents)
        if (key === 'userdata') return;
        
        // Handle email/emailid aliasing - show as "Email" regardless of API field name
        if (key === 'emailid' || key === 'email') {
          if (!addedFields.has('email_normalized')) {
            availableColumns.push({
              value: key,  // Use actual field name from API
              label: 'Email'
            });
            addedFields.add('email_normalized');
          }
          return;
        }
        
        // Add regular fields with proper display name
        let displayName = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        if (key === 'username') displayName = 'First Name';
        availableColumns.push({
          value: key,
          label: displayName
        });
        addedFields.add(key);
      });
      
      // Process userdata object fields (custom fields from CSV/XLS uploads)
      // These are important for variable mapping as they can be any column the user uploaded
      if (sampleContact.userdata && typeof sampleContact.userdata === 'object') {
        Object.keys(sampleContact.userdata).forEach(field => {
          const displayName = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          availableColumns.push({
            value: `userdata.${field}`,
            label: `${displayName} (Custom)`
          });
        });
      }
      
      // Also check for flattened userdata_* fields in case normalization was applied
      Object.keys(sampleContact).forEach(key => {
        if (key.startsWith('userdata_')) {
          const fieldName = key.replace('userdata_', '');
          const dotNotationField = `userdata.${fieldName}`;
          
          // Only add if not already added from userdata object
          if (!availableColumns.some(col => col.value === dotNotationField)) {
            const displayName = fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            availableColumns.push({
              value: dotNotationField,
              label: `${displayName} (Custom)`
            });
          }
        }
      });
    }

    return (
      <div className="broadcast-content-card">
        <div className="broadcast-section-header">
          <Title level={5} className="broadcast-section-title">Variables & Preview</Title>
          <Text className="broadcast-section-desc">Map template variables to contact fields and preview how your message will look.</Text>
        </div>
      <Row gutter={[24, 16]}>
        <Col xs={24} sm={24} md={15} lg={15} xl={15}>
          {/* ── BYO mode: show webhook mapping summary (configured in Audience step) ── */}
          {audienceSource === 'byo_webhook' ? (
            <Card title="Webhook Audience — Variable Mapping" size="small">
              <Alert
                message="Configured in the Audience step"
                description={
                  <span>
                    Variables are mapped to JSON keys from your webhook response.
                    Go back to <strong>Audience</strong> to change the mapping or run another test.
                  </span>
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              {/* Webhook URL summary */}
              <div style={{ marginBottom: 12 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Webhook URL</Text>
                <div style={{ fontFamily: 'monospace', fontSize: 12, background: '#f5f5f5', padding: '6px 10px', borderRadius: 4, wordBreak: 'break-all' }}>
                  {byoConfig.url || <Text type="danger">Not set</Text>}
                </div>
              </div>
              {/* Hard cap */}
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Hard Send Cap</Text>
                <div><Tag color="gold">{(byoConfig.hard_send_cap || 0).toLocaleString()} contacts max</Tag></div>
              </div>
              {/* Variable mapping summary */}
              {templateVariables.length > 0 ? (
                <div>
                  <Text strong style={{ fontSize: 13 }}>Variable → Webhook Key Mapping</Text>
                  <div style={{ marginTop: 8, padding: 12, background: '#f5f5f5', borderRadius: 6 }}>
                    {templateVariables.map(variable => {
                      const mappedKey = byoConfig.mapping?.[variable];
                      return (
                        <div key={variable} style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Tag color="blue">{`{{${variable}}}`}</Tag>
                          <span style={{ color: '#9ca3af' }}>→</span>
                          {mappedKey
                            ? <Tag color="green">{mappedKey}</Tag>
                            : <Tag color="orange">Not mapped — will be blank</Tag>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <Alert message="No Variables" description="This template has no variables." type="success" showIcon />
              )}
            </Card>
          ) : (
          <Card title="Variable Mapping" size="small">
            <Alert
              message="Map Template Variables"
              description={`Map template variables to columns from your selected address book: ${currentAddressBook?.bookname || 'None selected'}. The actual values from your selected contacts will be used in the messages.`}
              type="info"
              style={{ marginBottom: 16 }}
              showIcon
            />

            {/* Variable Mapping */}
            <Form layout="vertical">
              {templateVariables.length > 0 ? (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text strong>Map Variables to Columns:</Text>
                  {templateVariables.map(variable => {
                    const isMapped = templateMapping.mapping[variable];
                    
                    return (
                      <Form.Item 
                        key={variable} 
                        label={`Variable: ${variable}`}
                        style={{ marginBottom: 12 }}
                        validateStatus={!isMapped && validationTriggered ? 'error' : ''}
                        help={!isMapped && validationTriggered ? `Please map ${variable} to an address book column` : ''}
                      >
                        <Select
                          placeholder="Select address book column"
                          value={templateMapping.mapping[variable]}
                          onChange={(columnValue) => handleVariableMapping(variable, columnValue)}
                          disabled={!currentAddressBook || availableColumns.length === 0}
                          style={{ width: '100%' }}
                        >
                          {availableColumns.map(column => (
                            <Option key={column.value} value={column.value}>
                              {column.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    );
                  })}

                  {/* Dynamic media header URL — shown when template has an image/document/video header */}
                  {(() => {
                    const hType = getTemplateHeaderType(selectedTemplate);
                    if (!hType) return null;
                    const icon = hType === 'document' ? '📄' : hType === 'image' ? '🖼️' : '🎥';
                    return (
                      <Form.Item
                        label={
                          <span>
                            {icon} Header <Tag color={hType === 'document' ? 'green' : hType === 'image' ? 'blue' : 'purple'} style={{ textTransform: 'capitalize' }}>{hType}</Tag> URL
                            <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>(per-contact media link)</Text>
                          </span>
                        }
                        style={{ marginBottom: 12, borderTop: '1px dashed #d1d5db', paddingTop: 12 }}
                        validateStatus={!templateMapping.headerMediaColumn && validationTriggered ? 'error' : ''}
                        help={
                          !templateMapping.headerMediaColumn && validationTriggered
                            ? `Select the column that holds the ${hType} URL for each contact`
                            : <Text type="secondary" style={{ fontSize: 11 }}>Column must contain a publicly accessible https:// URL</Text>
                        }
                      >
                        <Select
                          placeholder={`Select column with ${hType} URL...`}
                          value={templateMapping.headerMediaColumn || undefined}
                          onChange={(columnValue) =>
                            setTemplateMapping(prev => ({
                              ...prev,
                              headerMediaColumn: columnValue,
                              headerMediaType: hType,
                            }))
                          }
                          disabled={!currentAddressBook || availableColumns.length === 0}
                          allowClear
                          style={{ width: '100%' }}
                        >
                          {availableColumns.map(column => (
                            <Option key={column.value} value={column.value}>
                              {column.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    );
                  })()}
                </Space>
              ) : (
                <Alert
                  message="No Variables"
                  description="This template doesn't contain any variables."
                  type="success"
                  showIcon
                />
              )}

              {/* Show mapping summary */}
              {(Object.keys(templateMapping.mapping).length > 0 || templateMapping.headerMediaColumn) && (
                <div style={{ marginTop: 16 }}>
                  <Text strong>Current Mapping:</Text>
                  <div style={{ marginTop: 8, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
                    {Object.entries(templateMapping.mapping).map(([variable, column]) => {
                      // Format display name for userdata fields
                      let displayName = column;
                      if (column.startsWith('userdata.')) {
                        displayName = column.replace('userdata.', '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                      } else {
                        displayName = column.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                      }
                      
                      return (
                        <div key={variable} style={{ marginBottom: 4 }}>
                          <Tag color="blue">{variable}</Tag> → <Tag color="green">{displayName}</Tag>
                        </div>
                      );
                    })}
                    {templateMapping.headerMediaColumn && (
                      <div style={{ marginBottom: 4, borderTop: '1px solid #e5e7eb', paddingTop: 4 }}>
                        <Tag color={templateMapping.headerMediaType === 'document' ? 'green' : templateMapping.headerMediaType === 'image' ? 'blue' : 'purple'}>
                          Header {templateMapping.headerMediaType}
                        </Tag>
                        {' '}→{' '}
                        <Tag color="orange">
                          {templateMapping.headerMediaColumn.startsWith('userdata.')
                            ? templateMapping.headerMediaColumn.replace('userdata.', '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) + ' (Custom)'
                            : templateMapping.headerMediaColumn.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Tag>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Form>
          </Card>
          )} {/* end BYO ternary */}
        </Col>

        <Col xs={24} sm={24} md={9} lg={9} xl={9}>
          <Card title="Message Preview" size="small">
            {/* WhatsApp Chat Preview */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div className="whatsapp-preview-container">
                {/* Chat Header */}
                <div className="chat-header">
                  <div className="chat-avatar whatsapp-avatar">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="20" cy="20" r="20" fill="#25D366"/>
                      <path d="M20 10C17.79 10 16 11.79 16 14C16 16.21 17.79 18 20 18C22.21 18 24 16.21 24 14C24 11.79 22.21 10 20 10ZM20 20C16.67 20 10 21.67 10 25V28H30V25C30 21.67 23.33 20 20 20Z" fill="white"/>
                    </svg>
                  </div>
                  <div className="chat-info">
                    <h3 className="chat-name">{(() => {
                      // Resolve company name from session storage (fallback to WhatsApp Business)
                      let companyName = 'WhatsApp Business';
                      try {
                        const usersStr = sessionStorage.getItem('user');
                        if (usersStr) {
                          const usersObj = JSON.parse(usersStr);
                          if (usersObj && usersObj.name) {
                            companyName = usersObj.name;
                          }
                        }
                      } catch (err) {
                        // keep default
                      }
                      return companyName;
                    })()}</h3>
                    <p className="chat-status">Template Preview</p>
                  </div>
                  <span className="template-category">{selectedTemplate?.templatecategory?.toUpperCase() || 'UTILITY'}</span>
                </div>

                {/* Chat Area */}
                <div className="chat-area">
                  <div className="message-container">
                    <div className="message-bubble">
                      {/* Header Content */}
                      {(() => {
                        const headerComponent = selectedTemplate?.component?.find(c => c.type === 'HEADER');
                        const mediaUrl = selectedTemplate?.header_media_url;
                        const format = selectedTemplate?.header_format || headerComponent?.format;
                        
                        if (mediaUrl && format === 'IMAGE') {
                          return (
                            <div className="message-header-content">
                              <img src={mediaUrl} alt="Template header" style={{ width: '100%', borderRadius: '8px 8px 0 0', maxHeight: 200, objectFit: 'cover', display: 'block' }} />
                            </div>
                          );
                        }
                        if (mediaUrl && format === 'VIDEO') {
                          return (
                            <div className="message-header-content">
                              <video src={mediaUrl} style={{ width: '100%', borderRadius: '8px 8px 0 0', maxHeight: 200, objectFit: 'cover', display: 'block' }} controls preload="metadata" />
                            </div>
                          );
                        }
                        if (mediaUrl && format === 'DOCUMENT') {
                          return (
                            <div className="message-header-content" style={{ padding: '12px', background: '#f0f4f8', borderRadius: '8px 8px 0 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: 28 }}>📄</span>
                              <a href={mediaUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#0036b0', wordBreak: 'break-all' }}>View Document</a>
                            </div>
                          );
                        }
                        if (headerComponent) {
                          return (
                            <div className="message-header-content">
                              {headerComponent.format === 'TEXT' && (
                                <div className="header-text">
                                  {headerComponent.text || 'Header text'}
                                </div>
                              )}
                              {headerComponent.format === 'IMAGE' && (
                                <div className="header-image">
                                  📷 Image Preview
                                </div>
                              )}
                              {headerComponent.format === 'VIDEO' && (
                                <div className="header-image">
                                  🎥 Video Preview
                                </div>
                              )}
                              {headerComponent.format === 'DOCUMENT' && (
                                <div className="header-image">
                                  📄 Document Preview
                                </div>
                              )}
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {/* Message Body */}
                      <div className="message-content">
                        {getPreviewContentWithMapping(selectedTemplate) || 'Template content will appear here'}
                      </div>

                      {/* Footer */}
                      {(() => {
                        const footerComponent = selectedTemplate?.component?.find(c => c.type === 'FOOTER');
                        if (footerComponent?.text) {
                          return (
                            <div className="message-footer-text">
                              {footerComponent.text}
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {/* Message Footer with Time and Checkmarks */}
                      <div className="message-footer">
                        <div className="message-time">
                          {moment().format('h:mm A')}
                          <svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.033l-.378.387a.32.32 0 0 0 .033.484l1.025.93a.32.32 0 0 0 .484-.033l6.272-8.048a.366.366 0 0 0-.063-.51zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033L3.724 9.587a.319.319 0 0 0-.484.033l-.378.387a.32.32 0 0 0 .033.484l1.025.93a.32.32 0 0 0 .484-.033L10.676 3.84a.366.366 0 0 0-.063-.51z" fill="#4fc3f7"/>
                          </svg>
                        </div>
                      </div>

                      {/* Buttons */}
                      {(() => {
                        const buttonComponents = selectedTemplate?.component?.filter(c => c.type === 'BUTTONS') || [];
                        if (buttonComponents.length > 0) {
                          return (
                            <div className="template-buttons">
                              {buttonComponents.map((buttonComponent, index) => 
                                buttonComponent.buttons?.map((button, btnIndex) => (
                                  <div key={`${index}-${btnIndex}`} className="template-button">
                                    {button.type === 'URL' && <LinkOutlined />}
                                    {button.type === 'PHONE_NUMBER' && <PhoneOutlined />}
                                    {button.text}
                                  </div>
                                ))
                              )}
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                </div>
                {/* Chat Footer */}
                <div className="chat-footer">
                  <div className="footer-input">
                    <span className="footer-placeholder">Type a message...</span>
                    <button className="send-button" type="button" aria-label="Send">➤</button>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Preview of how your message will appear to recipients
              </Text>
            </div>
          </Card>
        </Col>
      </Row>
      </div>
    );
  };

  const renderCampaignSummary = () => {
    const selectedTemplate = templates.find(t => t.templateid === campaignData.template.id);
    
    // Use the same preview function as the Variable Mapping step
    const finalPreviewContent = getPreviewContentWithMapping(selectedTemplate);

    return (
      <div className="broadcast-content-card">
        <div className="broadcast-section-header">
          <Title level={5} className="broadcast-section-title">Campaign Summary & Final Preview</Title>
          <Text className="broadcast-section-desc">Review everything before sending your campaign.</Text>
        </div>
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={24} md={15} lg={15} xl={15}>
            <Card size="small" title="Campaign Details">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text type="secondary">Campaign Name:</Text>
                  <div><Text strong>{campaignData.name || 'Untitled Campaign'}</Text></div>
                </div>
                <div>
                  <Text type="secondary">Description:</Text>
                  <div><Text>{campaignData.description || 'No description provided'}</Text></div>
                </div>
                <div>
                  <Text type="secondary">Recipients:</Text>
                  <div><Text strong>{campaignData.segment.type === 'all' ? totalContacts : allSelectedContactIds.size} contacts selected</Text></div>
                </div>
                <div>
                  <Text type="secondary">Template:</Text>
                  <div><Text strong>{campaignData.template.name || 'No template selected'}</Text></div>
                </div>
                <div>
                  <Text type="secondary">Variables:</Text>
                  <div>
                    {selectedTemplate?.variables && selectedTemplate.variables.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                        {selectedTemplate.variables.map(variable => {
                          // Get the mapped column name from templateMapping
                          const mappedColumn = templateMapping.mapping[variable];
                          const displayValue = mappedColumn || campaignData.template.variableSamples[variable] || 'Not mapped';
                          const tagColor = mappedColumn ? 'green' : 'orange';
                          
                          return (
                            <Tag key={variable} color={tagColor}>
                              {variable}: {mappedColumn ? `→ ${mappedColumn}` : displayValue}
                            </Tag>
                          );
                        })}
                      </div>
                    ) : (
                      <Text type="secondary">No variables in template</Text>
                    )}
                  </div>
                </div>
                <div>
                  <Text type="secondary">{planStatus ? 'Plan & Quota:' : 'Estimated Cost:'}</Text>
                  <div>
                    {(() => {
                      // Plan-based display (primary)
                      if (planStatus) {
                        return (
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                              <Tag color={planStatus.has_active_plan ? 'green' : 'red'}>
                                {planStatus.display_name || planStatus.plan_name || 'Plan'}
                              </Tag>
                              <Tag color="blue">{planStatus.billing_cycle}</Tag>
                            </div>
                            <div style={{ marginTop: 8 }}>
                              <Text strong style={{ color: '#0036b0', fontSize: '14px' }}>
                                {planStatus.whatsapp?.is_unlimited 
                                  ? '∞ Unlimited WhatsApp Campaigns' 
                                  : `${planStatus.whatsapp?.campaigns_remaining ?? 0} / ${planStatus.whatsapp?.campaign_limit ?? 0} campaigns remaining`}
                              </Text>
                            </div>
                            <div style={{ marginTop: 4 }}>
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                This campaign will use 1 of your {planStatus.whatsapp?.is_unlimited ? 'unlimited' : (planStatus.whatsapp?.campaign_limit || 0)} campaign quota
                              </Text>
                            </div>
                            {planStatus.days_remaining != null && (
                              <div style={{ marginTop: 4 }}>
                                <Text type="secondary" style={{ fontSize: '11px' }}>
                                  {planStatus.days_remaining} days remaining in billing cycle
                                </Text>
                              </div>
                            )}
                          </div>
                        );
                      }
                      
                      if (rateCalculationLoading) {
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Spin size="small" />
                            <Text type="secondary">Calculating rates...</Text>
                          </div>
                        );
                      }
                      
                      // Rate-based display (informational only — billing is quota-based)
                      if (estimatedCost !== null && rateDetails) {
                        const hasDiscount = campaignData.discountInfo && campaignData.discountInfo.discountPercentage > 0;
                        
                        return (
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <Text strong style={{ color: '#0036b0' }}>
                                ₹{estimatedCost.toFixed(2)}
                              </Text>
                              {hasDiscount && (
                                <Tooltip title="Click for discount details">
                                  <Button 
                                    type="text" 
                                    size="small" 
                                    icon={<InfoCircleOutlined />}
                                    onClick={fetchDiscountDetails}
                                    style={{ padding: 0, minWidth: 'auto' }}
                                  />
                                </Tooltip>
                              )}
                            </div>
                            
                            {hasDiscount && (
                              <div style={{ marginTop: 4 }}>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  Original: ₹{campaignData.discountInfo.originalAmount.toFixed(2)} | 
                                  Discount ({campaignData.discountInfo.discountPercentage}%): -₹{campaignData.discountInfo.discountAmount.toFixed(2)}
                                </Text>
                              </div>
                            )}
                            
                            {rateDetails.contactsdetails && rateDetails.contactsdetails.length > 0 && (
                              <div style={{ marginTop: 8 }}>
                                <Text type="secondary" style={{ fontSize: '12px' }}>Rate breakdown:</Text>
                                <div style={{ marginTop: 4 }}>
                                  {rateDetails.contactsdetails.map((detail, index) => (
                                    <div key={index} style={{ fontSize: '11px', color: '#666' }}>
                                      {detail.countrycode}: {detail.count} contacts × ₹{parseFloat(detail.pricepermessage || 0).toFixed(4)} = ₹{parseFloat(detail.totalprice || 0).toFixed(2)}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      }
                      
                      // Fallback
                      if (rateLoading) {
                        return <Text type="secondary">Loading...</Text>;
                      }
                      
                      return (
                        <div>
                          <Text strong style={{ color: '#666' }}>₹0.00</Text>
                          <div style={{ marginTop: 4 }}>
                            <Text type="secondary" style={{ fontSize: '11px' }}>
                              Cost calculation unavailable. Please proceed to create campaign.
                            </Text>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
                
                {/* Discount / Plan Features Section */}
                <Divider style={{ margin: '12px 0' }} />
                <div>
                  {planStatus ? (
                    <div>
                      <Text type="secondary">Plan Features:</Text>
                      <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {(planStatus.features || []).slice(0, 5).map((feature, idx) => (
                          <Tag key={idx} color="blue" style={{ fontSize: '11px' }}>{feature}</Tag>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Text type="secondary">Discount Information:</Text>
                        <Tooltip title="Click for detailed discount information">
                          <Button 
                            type="text" 
                            size="small" 
                            icon={<InfoCircleOutlined />}
                            onClick={fetchDiscountDetails}
                            style={{ padding: 0, minWidth: 'auto', color: '#0036b0' }}
                          />
                        </Tooltip>
                      </div>
                      <div style={{ marginTop: 4 }}>
                        {campaignData.discountInfo && campaignData.discountInfo.discountPercentage > 0 ? (
                          <div>
                            <Text style={{ fontSize: '12px', color: '#52c41a' }}>
                              🎉 You have a {campaignData.discountInfo.discountPercentage}% discount applied!
                            </Text>
                          </div>
                        ) : (
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            No discount currently applied to this campaign.
                          </Text>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* WhatsApp Pricing Calculator */}
                <Divider style={{ margin: '12px 0' }} />
                <Button
                  type="dashed"
                  icon={<InfoCircleOutlined />}
                  onClick={() => setPricingCalcVisible(true)}
                  block
                  style={{ borderColor: '#25D366', color: '#25D366' }}
                >
                  WhatsApp Pricing Calculator
                </Button>
              </Space>
            </Card>

            <Card size="small" title="Campaign Status" style={{ marginTop: 16 }}>
              {(() => {
                if (rateLoading || rateCalculationLoading) {
                  return <Text type="secondary">Loading campaign status...</Text>;
                }

                // Plan-based quota check (primary)
                if (planStatus) {
                  if (!planStatus.has_active_plan) {
                    return (
                      <Alert
                        message="No Active Plan"
                        description={
                          <div>
                            <p>You need an active subscription plan to create WhatsApp campaigns.</p>
                            <Button type="primary" style={{ marginTop: 12 }} onClick={() => window.location.href = '/dashboard/pricing'}>
                              Purchase a Plan
                            </Button>
                          </div>
                        }
                        type="error"
                        showIcon
                      />
                    );
                  }
                  if (!planStatus.whatsapp?.is_unlimited && (planStatus.whatsapp?.campaigns_remaining || 0) <= 0) {
                    return (
                      <Alert
                        message="WhatsApp Campaign Quota Exhausted"
                        description={
                          <div>
                            <p>You have used all {planStatus.whatsapp?.campaign_limit || 0} campaigns in this billing cycle.</p>
                            <Button type="primary" style={{ marginTop: 12 }} onClick={() => window.location.href = '/dashboard/pricing'}>
                              Upgrade Plan
                            </Button>
                          </div>
                        }
                        type="error"
                        showIcon
                      />
                    );
                  }
                  return (
                    <Alert
                      message="Campaign Ready"
                      description={`Your campaign is ready. ${planStatus.whatsapp?.is_unlimited ? 'You have unlimited WhatsApp campaigns.' : `${planStatus.whatsapp?.campaigns_remaining || 0} of ${planStatus.whatsapp?.campaign_limit || 0} campaigns remaining this cycle.`}`}
                      type="success"
                      showIcon
                    />
                  );
                }
                
                // No active plan — prompt to subscribe
                return (
                  <Alert
                    message="No Active Plan"
                    description="You need an active subscription plan to create WhatsApp campaigns."
                    type="warning"
                    showIcon
                    action={
                      <Button size="small" onClick={() => { window.location.href = '/dashboard/pricing'; }}>View Plans</Button>
                    }
                  />
                );
              })()}
            </Card>

            {/* Discount Modal */}
            <Modal
              title="Discount Details"
              visible={discountModalVisible}
              onCancel={() => setDiscountModalVisible(false)}
              footer={null}
              width={800}
            >
              <Alert
                message="Note: Meta offers discounts only on Utility and Authentication categories, not on Marketing."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              
              <Space direction="horizontal" style={{ marginBottom: 16 }}>
                <Button 
                  type={selectedCategory === 'Utility' ? 'primary' : 'default'}
                  onClick={() => setSelectedCategory('Utility')}
                >
                  Utility
                </Button>
                <Button 
                  type={selectedCategory === 'Authentication' ? 'primary' : 'default'}
                  onClick={() => setSelectedCategory('Authentication')}
                >
                  Authentication
                </Button>
              </Space>
              
              <Select
                style={{ width: '100%', maxWidth: 200, marginBottom: 16 }}
                placeholder="Select Country/Region"
                value={selectedCountryCode}
                onChange={setSelectedCountryCode}
                options={countryOptions}
              />
              
              {discountLoading ? (
                <Spin />
              ) : filteredDiscountRows.length > 0 ? (
                <>
                  <Title level={5}>Messages per month</Title>
                  <Title level={5}>What we charge</Title>
                  <Table
                    columns={[
                      { title: 'From', dataIndex: 'from', key: 'from' },
                      { title: 'To', dataIndex: 'to', key: 'to' },
                      { 
                        title: 'Rate', 
                        dataIndex: 'rate', 
                        key: 'rate',
                        render: (value, record) => {
                          const num = Number(value);
                          return `${record.currency || ''}${isNaN(num) ? 'N/A' : num.toFixed(5)}`;
                        }
                      },
                      { 
                        title: 'Discounted Rate', 
                        dataIndex: 'discountrate', 
                        key: 'discountrate',
                        render: (value, record) => {
                          const num = Number(value);
                          return `${record.currency || ''}${isNaN(num) ? 'N/A' : num.toFixed(5)}`;
                        }
                      },
                      { title: 'versus List rate', dataIndex: 'versus', key: 'versus' }
                    ]}
                    dataSource={filteredDiscountRows}
                    pagination={false}
                    size="small"
                  />
                </>
              ) : (
                <Empty description="No discounts available for selected category and country" />
              )}
            </Modal>
          </Col>

          <Col xs={24} sm={24} md={9} lg={9} xl={9}>
            <Card size="small" title="Message Preview">
              {/* WhatsApp Chat Preview */}
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <div className="whatsapp-preview-container">
                  {/* Chat Header */}
                  <div className="chat-header">
                    <div className="chat-avatar whatsapp-avatar">
                      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="20" cy="20" r="20" fill="#25D366"/>
                        <path d="M20 10C17.79 10 16 11.79 16 14C16 16.21 17.79 18 20 18C22.21 18 24 16.21 24 14C24 11.79 22.21 10 20 10ZM20 20C16.67 20 10 21.67 10 25V28H30V25C30 21.67 23.33 20 20 20Z" fill="white"/>
                      </svg>
                    </div>
                    <div className="chat-info">
                      <h3 className="chat-name">{(() => {
                        // Resolve company name from session storage (fallback to WhatsApp Business)
                        let companyName = 'WhatsApp Business';
                        try {
                          const usersStr = sessionStorage.getItem('user');
                          if (usersStr) {
                            const usersObj = JSON.parse(usersStr);
                            if (usersObj && usersObj.name) {
                              companyName = usersObj.name;
                            }
                          }
                        } catch (err) {
                          // keep default
                        }
                        return companyName;
                      })()}</h3>
                      <p className="chat-status">Template Preview</p>
                    </div>
                    <span className="template-category">{selectedTemplate?.templatecategory?.toUpperCase() || 'MARKETING'}</span>
                  </div>

                  {/* Chat Area */}
                  <div className="chat-area">
                    <div className="message-container">
                      <div className="message-bubble">
                        {/* Header Content */}
                        {(() => {
                          const headerComponent = selectedTemplate?.component?.find(c => c.type === 'HEADER');
                          const mediaUrl = selectedTemplate?.header_media_url;
                          const format = selectedTemplate?.header_format || headerComponent?.format;
                          
                          if (mediaUrl && format === 'IMAGE') {
                            return (
                              <div className="message-header-content">
                                <img src={mediaUrl} alt="Template header" style={{ width: '100%', borderRadius: '8px 8px 0 0', maxHeight: 200, objectFit: 'cover', display: 'block' }} />
                              </div>
                            );
                          }
                          if (mediaUrl && format === 'VIDEO') {
                            return (
                              <div className="message-header-content">
                                <video src={mediaUrl} style={{ width: '100%', borderRadius: '8px 8px 0 0', maxHeight: 200, objectFit: 'cover', display: 'block' }} controls preload="metadata" />
                              </div>
                            );
                          }
                          if (mediaUrl && format === 'DOCUMENT') {
                            return (
                              <div className="message-header-content" style={{ padding: '12px', background: '#f0f4f8', borderRadius: '8px 8px 0 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 28 }}>📄</span>
                                <a href={mediaUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#0036b0', wordBreak: 'break-all' }}>View Document</a>
                              </div>
                            );
                          }
                          if (headerComponent) {
                            return (
                              <div className="message-header-content">
                                {headerComponent.format === 'TEXT' && (
                                  <div className="header-text">
                                    {headerComponent.text || 'Header text'}
                                  </div>
                                )}
                                {headerComponent.format === 'IMAGE' && (
                                  <div className="header-image">
                                    📷 Image Preview
                                  </div>
                                )}
                                {headerComponent.format === 'VIDEO' && (
                                  <div className="header-image">
                                    🎥 Video Preview
                                  </div>
                                )}
                                {headerComponent.format === 'DOCUMENT' && (
                                  <div className="header-image">
                                    📄 Document Preview
                                  </div>
                                )}
                              </div>
                            );
                          }
                          return null;
                        })()}

                        {/* Message Body */}
                        <div className="message-content">
                          {finalPreviewContent || 'Template content will appear here'}
                        </div>

                        {/* Footer */}
                        {(() => {
                          const footerComponent = selectedTemplate?.component?.find(c => c.type === 'FOOTER');
                          if (footerComponent?.text) {
                            return (
                              <div className="message-footer-text">
                                {footerComponent.text}
                              </div>
                            );
                          }
                          return null;
                        })()}

                        {/* Message Footer with Time and Checkmarks */}
                        <div className="message-footer">
                          <div className="message-time">
                            {moment().format('h:mm A')}
                            <svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.033l-.378.387a.32.32 0 0 0 .033.484l1.025.93a.32.32 0 0 0 .484-.033l6.272-8.048a.366.366 0 0 0-.063-.51zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033L3.724 9.587a.319.319 0 0 0-.484.033l-.378.387a.32.32 0 0 0 .033.484l1.025.93a.32.32 0 0 0 .484-.033L10.676 3.84a.366.366 0 0 0-.063-.51z" fill="#4fc3f7"/>
                            </svg>
                          </div>
                        </div>

                        {/* Buttons */}
                        {(() => {
                          const buttonComponents = selectedTemplate?.component?.filter(c => c.type === 'BUTTONS') || [];
                          if (buttonComponents.length > 0) {
                            return (
                              <div className="template-buttons">
                                {buttonComponents.map((buttonComponent, index) => 
                                  buttonComponent.buttons?.map((button, btnIndex) => (
                                    <div key={`${index}-${btnIndex}`} className="template-button">
                                      {button.type === 'URL' && <LinkOutlined />}
                                      {button.type === 'PHONE_NUMBER' && <PhoneOutlined />}
                                      {button.text}
                                    </div>
                                  ))
                                )}
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* Chat Footer */}
                  <div className="chat-footer">
                    <div className="footer-input">
                      <span className="footer-placeholder">Type a message...</span>
                      <button className="send-button" type="button" aria-label="Send">➤</button>
                    </div>
                  </div>
                </div>
              </div>
              
              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Final preview of how your campaign message will appear to recipients
                </Text>
              </div>
            </Card>
          </Col>
        </Row>
        {/* AI behaviour for this campaign — shown at SEND time so the user can
            scope which already-configured skills/KB the AI should use when
            recipients reply, or disable auto-reply entirely. New skills / KB
            entries are NOT created here; they are managed in AI Chatbot. */}
        <CampaignAIBehaviourPanel
          value={aiBehaviour}
          onChange={setAiBehaviour}
        />
      </div>
    );
  };

  // Navigation handlers
  const validateCurrentStep = () => {
    switch (currentStep) {
      case 0: // Campaign Info
        if (!campaignData.name?.trim()) {
          // Set focus to campaign name field
          const campaignNameInput = document.querySelector('input[placeholder*="Enter campaign name"]');
          if (campaignNameInput) {
            campaignNameInput.focus();
          }
          return false;
        }
        break;
      case 1: // Choose Template (was step 2)
        if (!campaignData.template.id) {
          // Scroll to template selection area
          const templateArea = document.querySelector('[title="Template Selection"]');
          if (templateArea) {
            templateArea.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return false;
        }
        break;
      case 2: // Audience — Contact Selection or BYO Webhook (was step 1)
        if (audienceSource === 'byo_webhook') {
          if (!byoConfig.url?.trim()) {
            message.error('Please enter a Webhook URL for your audience source');
            return false;
          }
          try { new URL(byoConfig.url); } catch {
            message.error('Webhook URL is not a valid URL (must start with https://)');
            return false;
          }
          if (!byoConfig.hard_send_cap || Number(byoConfig.hard_send_cap) <= 0) {
            message.error('Hard Send Cap must be greater than 0');
            return false;
          }
          break;
        }
        if (allSelectedContactIds.size === 0) {
          // Scroll to contacts table
          const contactsTable = document.querySelector('.ant-table-wrapper');
          if (contactsTable) {
            contactsTable.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          setValidationTriggered(true);
          return false;
        }
        break;
      case 3: // Variables & Preview
        const selectedTemplate = templates.find(t => t.templateid === campaignData.template.id);
        const templateVariables = extractTemplateVariables(selectedTemplate);

        if (audienceSource === 'byo_webhook') {
          // BYO: mapping is optional (webhook keys auto-fill variables at send time)
          // No address book required — just pass through
          break;
        }
        
        if (templateVariables.length > 0) {
          // Check if address book is selected
          if (!currentAddressBook) {
            message.error('Please select an address book to map variables');
            return false;
          }
          
          // Check if all variables are mapped
          const unmappedVariables = templateVariables.filter(variable => 
            !templateMapping.mapping[variable]
          );
          
          if (unmappedVariables.length > 0) {
            message.error(`Please map all variables: ${unmappedVariables.join(', ')}`);
            return false;
          }
        }

        // Check header media column when the template has a media header
        if (getTemplateHeaderType(selectedTemplate) && !templateMapping.headerMediaColumn) {
          message.error('This template has a media header — please select the address book column that contains the media URL');
          return false;
        }
        break;
      case 4: // Final Review - Additional validation before creation
        if (!campaignData.name?.trim()) {
          return false;
        }
        // BYO campaigns do not use the contacts list — skip that check
        if (audienceSource !== 'byo_webhook' && allSelectedContactIds.size === 0) {
          return false;
        }
        if (!campaignData.template.id) {
          return false;
        }
        if (!currentUser?.companyid) {
          Modal.error({
            title: 'Authentication Error',
            content: 'Company information is missing. Please log in again.',
            onOk: () => {
              // Redirect to login or refresh page
              window.location.reload();
            }
          });
          return false;
        }
        // Plan-based quota check
        if (planStatus) {
          if (!planStatus.has_active_plan) {
            Modal.error({
              title: 'No Active Plan',
              content: 'You need an active subscription plan to create WhatsApp campaigns.',
              okText: 'View Plans',
              onOk: () => { window.location.href = '/dashboard/pricing'; }
            });
            return false;
          }
          if (!planStatus.whatsapp?.is_unlimited && (planStatus.whatsapp?.campaigns_remaining || 0) <= 0) {
            Modal.error({
              title: 'Campaign Quota Exhausted',
              content: `You have used all ${planStatus.whatsapp?.campaign_limit || 0} WhatsApp campaigns this billing cycle.`,
              okText: 'Upgrade Plan',
              onOk: () => { window.location.href = '/dashboard/pricing'; }
            });
            return false;
          }
        }
        break;
      default:
        break;
    }
    return true;
  };

  const scrollToTop = () => {
    const contentEl = document.querySelector('.broadcast-step-content');
    if (contentEl) contentEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNext = async () => {
    setValidationTriggered(true);
    if (validateCurrentStep() && currentStep < 4) {
      // Call rate calculation when moving from Audience (step 2) to Variables & Preview (step 3)
      if (currentStep === 2) {
        await calculateEstimatedCost();
      }
      
      setCurrentStep(currentStep + 1);
      setValidationTriggered(false); // Reset validation trigger for next step
      scrollToTop();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setValidationTriggered(false); // Reset validation trigger when going back
      scrollToTop();
    }
  };

  const buildCampaignRequestBody = () => {
    const baseBody = {
      campaignname: campaignData.name,
      templateid: campaignData.template.id
    };

    // Phase 2.5 — BYO Webhook campaigns bypass address-book + contact selection.
    if (audienceSource === 'byo_webhook') {
      baseBody.source = 'byo_webhook';
      baseBody.audience_webhook_url = byoConfig.url;
      baseBody.audience_webhook_auth = byoConfig.auth || { type: 'none' };
      baseBody.hard_send_cap = byoConfig.hard_send_cap;
      if (byoConfig.saved_preset_id) baseBody.saved_preset_id = byoConfig.saved_preset_id;
      if (byoConfig.mapping && Object.keys(byoConfig.mapping).length) {
        baseBody.templatemapping = {
          variabletype: 'webhook',
          mapping: byoConfig.mapping,
        };
      }
      // No selectionType / userids / bookid for BYO — backend streams from webhook
      return baseBody;
    }

    // Add address book information if available
    if (currentAddressBook?.bookid) {
      baseBody.bookid = currentAddressBook.bookid;
    }

    // Add template mapping if variables exist and are mapped
    if (Object.keys(templateMapping.mapping).length > 0 || templateMapping.headerMediaColumn) {
      baseBody.templatemapping = {
        variabletype: templateMapping.variabletype,
        mapping: templateMapping.mapping,
      };
      // Include dynamic media header info when the template has a media header
      if (templateMapping.headerMediaColumn) {
        baseBody.templatemapping.headerMediaColumn = templateMapping.headerMediaColumn;
        baseBody.templatemapping.headerMediaType = templateMapping.headerMediaType || getTemplateHeaderType(
          templates.find(t => t.templateid === campaignData.template.id)
        );
      }
    }

    // Add rate details if available
    if (rateDetails) {
      const selectedTemplate = templates.find(t => t.templateid === campaignData.template.id);
      const templateCategory = selectedTemplate?.category?.toLowerCase() || 'marketing';
      
      baseBody.ratedetails = {
        category: templateCategory,
        contactsdetails: rateDetails.contactsdetails || [],
        totalContacts: rateDetails.totalContacts || 0,
        finalPrice: rateDetails.finalPrice || 0
      };

      // Add discount information if available
      if (campaignData.discountInfo && campaignData.discountInfo.discountPercentage > 0) {
        baseBody.ratedetails.discountid = campaignData.discountInfo.discountid;
        baseBody.ratedetails.discountPercentage = campaignData.discountInfo.discountPercentage;
        baseBody.ratedetails.discountAmount = campaignData.discountInfo.discountAmount;
        baseBody.ratedetails.discountedAmount = campaignData.discountInfo.discountedAmount;
      }
    }

    // Log the complete payload for debugging
    console.log('Campaign Creation Payload:', JSON.stringify(baseBody, null, 2));

    // Determine selection type based on campaign segment configuration
    const segmentType = campaignData.segment.type;
    
    if (segmentType === 'all') {
      // Select all contacts - only when segment type is explicitly 'all'
      return {
        ...baseBody,
        selectionType: 'all'
      };
    } else if (segmentType === 'custom' && (contactFilters.usercategory || campaignData.segment.filters.category?.length > 0)) {
      // Filter by category
      const category = contactFilters.usercategory || campaignData.segment.filters.category[0] || 'vip';
      return {
        ...baseBody,
        selectionType: 'filter',
        category: category.toLowerCase()
      };
    } else {
      // One by one selection with specific user IDs (for custom individual selection)
      return {
        ...baseBody,
        selectionType: 'selection',
        userids: Array.from(allSelectedContactIds) || []
      };
    }
  };

  const validateCampaignRequest = (requestBody) => {
    if (!requestBody.campaignname) {
      return { isValid: false, message: 'Campaign name is required' };
    }
    if (!requestBody.templateid) {
      return { isValid: false, message: 'Template selection is required' };
    }

    // Phase 2.5 — BYO Webhook validation
    if (requestBody.source === 'byo_webhook') {
      if (!requestBody.audience_webhook_url) {
        return { isValid: false, message: 'Webhook URL is required for BYO audience' };
      }
      try { new URL(requestBody.audience_webhook_url); }
      catch { return { isValid: false, message: 'Webhook URL is not a valid URL' }; }
      if (!requestBody.hard_send_cap || Number(requestBody.hard_send_cap) <= 0) {
        return { isValid: false, message: 'Hard send cap must be greater than 0' };
      }
      return { isValid: true };
    }

    // Validate based on selection type
    switch (requestBody.selectionType) {
      case 'filter':
        if (!requestBody.category) {
          return { isValid: false, message: 'Category is required for filter selection' };
        }
        break;
      case 'selection':
        if (!requestBody.userids || requestBody.userids.length === 0) {
          return { isValid: false, message: 'At least one contact must be selected' };
        }
        break;
      case 'all':
        // No additional validation needed for 'all' selection
        break;
      default:
        return { isValid: false, message: 'Invalid selection type' };
    }
    
    return { isValid: true };
  };

  const handleFinish = () => {
    // Final validation before showing confirmation
    if (!validateCurrentStep()) {
      return;
    }
    
    // Plan-based: check plan quota
    if (planStatus) {
      if (!planStatus.has_active_plan) {
        Modal.error({
          title: 'No Active Plan',
          content: 'You need an active subscription plan to create WhatsApp campaigns.',
          okText: 'View Plans',
          onOk: () => { window.location.href = '/dashboard/pricing'; }
        });
        return;
      }
      if (!planStatus.whatsapp?.is_unlimited && (planStatus.whatsapp?.campaigns_remaining || 0) <= 0) {
        Modal.error({
          title: 'Campaign Quota Exhausted',
          content: `You have used all ${planStatus.whatsapp?.campaign_limit || 0} WhatsApp campaigns this billing cycle.`,
          okText: 'Upgrade Plan',
          onOk: () => { window.location.href = '/dashboard/pricing'; }
        });
        return;
      }
      // Plan is active and has remaining quota — proceed
    } else {
      // No active plan — block campaign creation
      Modal.error({
        title: 'No Active Plan',
        content: 'You need an active subscription plan to create WhatsApp campaigns.',
        okText: 'View Plans',
        onOk: () => { window.location.href = '/dashboard/pricing'; }
      });
      return;
    }
    
    Modal.confirm({
      title: null,
      icon: null,
      width: 440,
      className: 'bc-confirm-modal',
      content: (
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px', border: '3px solid #bfdbfe'
          }}>
            <CheckCircleOutlined style={{ fontSize: 28, color: '#0036b0' }} />
          </div>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#1e293b', marginBottom: 6 }}>
            Ready to Create Campaign?
          </div>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20, lineHeight: 1.5 }}>
            Your campaign will be saved as a draft. You can review and send it from the campaigns list.
          </div>
          <div style={{
            background: '#f8fafc', borderRadius: 10, padding: '14px 16px',
            border: '1px solid #e2e8f0', textAlign: 'left'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>Campaign</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{campaignData.name || '—'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>Recipients</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                {audienceSource === 'byo_webhook'
                  ? `via webhook · cap ${(byoConfig.hard_send_cap || 0).toLocaleString()}`
                  : `${allSelectedContactIds.size} contact${allSelectedContactIds.size !== 1 ? 's' : ''}`}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>Template</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{campaignData.template.name || '—'}</span>
            </div>
          </div>
        </div>
      ),
      okText: 'Create Campaign',
      cancelText: 'Cancel',
      okButtonProps: {
        style: {
          background: '#0036b0', borderColor: '#0036b0', borderRadius: 8,
          height: 40, fontWeight: 600, fontSize: 14, padding: '0 24px'
        }
      },
      cancelButtonProps: {
        style: { borderRadius: 8, height: 40, fontSize: 14, padding: '0 20px' }
      },
      onOk: async () => {
        try {
          setLoading(true);
          
          // Build request body based on selection type
          const requestBody = buildCampaignRequestBody();

          // Validate the request body
          const validation = validateCampaignRequest(requestBody);
          if (!validation.isValid) {
            message.error(validation.message);
            return;
          }

          console.log('Creating campaign with data:', requestBody);
          
          // API call to create campaign
          const response = await axiosInstance.post('/campaign/create', requestBody);
          
          if (response.data.statuscode == 200 ) {
            message.success(response.data.message);
            // Backend returns: { entity: { campaign: { campaignid } } }
            const newCampaignId = response.data?.entity?.campaign?.campaignid
              || response.data?.entity?.campaignid
              || response.data.campaignId
              || response.data.id;
            // Persist per-campaign AI behaviour override (best-effort; non-fatal)
            const hasAIOverride = aiBehaviour.is_one_way
              || (aiBehaviour.skill_keys && aiBehaviour.skill_keys.length > 0)
              || (aiBehaviour.kb_tags && aiBehaviour.kb_tags.length > 0);
            if (newCampaignId && hasAIOverride) {
              try {
                await aiConfigApi.upsertCampaignAIConfig(newCampaignId, {
                  skill_keys: aiBehaviour.skill_keys || [],
                  kb_tags: aiBehaviour.kb_tags || [],
                  is_one_way: !!aiBehaviour.is_one_way,
                });
                console.log('[AI Behaviour] Saved override for campaign', newCampaignId);
              } catch (e) {
                console.warn('Failed to save campaign AI config', e);
                message.warning('Campaign created, but AI override was not saved. Edit it from the campaign page.');
              }
            } else if (hasAIOverride && !newCampaignId) {
              console.warn('[AI Behaviour] Override skipped — campaign id missing in response', response.data);
              message.warning('Campaign created, but AI override could not be saved (missing id).');
            }
            onSave && onSave({
              ...campaignData,
              id: newCampaignId,
              status: 'draft'
            });
            onBack();// Redirect to campaigns list
          } else {
            message.error(response.data?.message || 'Failed to create campaign');
          }
          
        } catch (error) {
          console.error('Error creating campaign:', error);
          if (error.response?.data?.message) {
            message.error(error.response.data.message);
          } else {
            message.error('Failed to create campaign. Please try again.');
          }
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // Phase 2.5 — Audience step wraps the existing contact selection with a source picker.
  // If user picks BYO, we hide the contact table and show the webhook configurator instead.
  const renderAudienceStep = () => {
    const selectedTemplate = templates.find(t => t.templateid === campaignData.template.id);
    const tplVariables = selectedTemplate ? extractTemplateVariables(selectedTemplate) : [];
    return (
      <div>
        <AudienceSourceSelector
          value={audienceSource}
          onChange={(v) => {
            setAudienceSource(v);
            // when switching to BYO clear stale contact selection
            if (v === 'byo_webhook') {
              setSelectedRowKeys([]);
              setAllSelectedContactIds(new Set());
              setTotalSelectedContacts([]);
            }
          }}
        />
        <div style={{ marginTop: 24 }}>
          {audienceSource === 'byo_webhook' && byoEnabled ? (
            <ByoWebhookConfigurator
              templateId={campaignData?.template?.id}
              templateVariables={tplVariables}
              value={byoConfig}
              onChange={setByoConfig}
            />
          ) : (
            renderContactSelection()
          )}
        </div>
      </div>
    );
  };

  const steps = [
    {
      title: 'Campaign Info',
      subtitle: 'Name & description',
      content: renderCampaignInfo(),
    },
    {
      title: 'Choose Template',
      subtitle: 'Pick a message template',
      content: renderTemplateSelection(),
    },
    {
      title: 'Audience',
      subtitle: 'Source & recipients',
      content: renderAudienceStep(),
    },
    {
      title: 'Variables & Preview',
      subtitle: 'Map fields & preview',
      content: renderVariablesAndPreview(),
    },
    {
      title: 'Final Review',
      subtitle: 'Review & send',
      content: renderCampaignSummary(),
    },
  ];

  const stepIndicatorSteps = steps.map((step, index) => ({
    number: index + 1,
    title: step.title,
  }));

  const handleStepIndicatorClick = (stepNumber) => {
    const index = stepNumber - 1;
    if (index < currentStep) {
      setCurrentStep(index);
      scrollToTop();
    }
  };

  return (
    <div className="advanced-template-builder advanced-broadcast-builder">
      {/* Top Header */}
      <div className="broadcast-top-header">
        <button className="broadcast-back-btn" onClick={onBack}>
          <ArrowLeftOutlined />
        </button>
        <Title level={4} className="broadcast-top-header-title">
          Create Campaign
        </Title>
      </div>

      {/* Two-Column Layout */}
      <div className="broadcast-layout">
        {/* Right Main Content */}
        <div className="broadcast-main">
          <div className="broadcast-horizontal-stepper">
            <TemplateWizardStepIndicator
              steps={stepIndicatorSteps}
              currentStep={currentStep + 1}
              onStepClick={handleStepIndicatorClick}
              isStepCompleted={(stepNumber) => currentStep + 1 > stepNumber}
              maxWidth="100%"
            />
          </div>

          {/* Step Content */}
          <div className="broadcast-step-content" key={currentStep}>
            {steps[currentStep].content}
          </div>

          {/* Navigation (inside right panel) */}
          <div className="broadcast-step-nav">
            <Button onClick={handlePrev} disabled={currentStep === 0}>
              Previous
            </Button>
              {currentStep < steps.length - 1 && (
                <Button type="primary" onClick={handleNext}>
                  Next
                </Button>
              )}
              {currentStep === steps.length - 1 && (() => {
                const isPlanBased = !!planStatus;
                let isBlocked = false;
                let blockReason = '';
                let blockButtonText = 'Create Campaign';
                
                if (isPlanBased) {
                  if (!planStatus.has_active_plan) {
                    isBlocked = true;
                    blockReason = 'No active plan. Please purchase a plan to create campaigns.';
                    blockButtonText = 'No Active Plan';
                  } else if (!planStatus.whatsapp?.is_unlimited && (planStatus.whatsapp?.campaigns_remaining || 0) <= 0) {
                    isBlocked = true;
                    blockReason = `Campaign quota exhausted. You have used all ${planStatus.whatsapp?.campaign_limit || 0} campaigns.`;
                    blockButtonText = 'Quota Exhausted';
                  }
                } else {
                  // No active plan — block campaign creation
                  isBlocked = true;
                  blockReason = 'No active subscription plan. Purchase a plan to create campaigns.';
                  blockButtonText = 'No Active Plan';
                }
                
                return (
                  <Tooltip title={isBlocked ? blockReason : ''}>
                    <Button 
                      type="primary" 
                      onClick={() => {
                        if (validateCurrentStep()) {
                          handleFinish();
                        }
                      }} 
                      loading={loading}
                      disabled={isBlocked}
                      danger={isBlocked}
                    >
                      {isBlocked ? blockButtonText : 'Create Campaign'}
                    </Button>
                  </Tooltip>
                );
              })()}
          </div>
        </div>
      </div>

      {/* Template Preview Modal */}
      <Modal
        title={`Template Preview: ${selectedTemplatePreview?.templatename || ''}`}
        open={templatePreviewVisible}
        onCancel={() => setTemplatePreviewVisible(false)}
        className="bc-template-preview-modal"
        footer={[
          <Button key="close" onClick={() => setTemplatePreviewVisible(false)}>
            Close
          </Button>,
          <Button
            key="select"
            type="primary"
            onClick={() => {
              if (selectedTemplatePreview) {
                setCampaignData(prev => ({
                  ...prev,
                  template: {
                    ...prev.template,
                    id: selectedTemplatePreview.templateid,
                    name: selectedTemplatePreview.templatename,
                    content: selectedTemplatePreview.templatecontent || selectedTemplatePreview.content || '',
                    variables: selectedTemplatePreview.variables
                  }
                }));
                setTemplatePreviewVisible(false);
              }
            }}
          >
            Select This Template
          </Button>
        ]}
        width="90%"
        style={{ maxWidth: 600 }}
      >
        {selectedTemplatePreview && (
          <div>
            <Row className="bc-template-preview-meta" gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={8}>
                <Text strong>Language:</Text>
                <div><Tag color="blue">{selectedTemplatePreview.templatelanguage?.toUpperCase()}</Tag></div>
              </Col>
              <Col xs={24} sm={8}>
                <Text strong>Category:</Text>
                <div><Tag color="purple">{selectedTemplatePreview.templatecategory?.toUpperCase()}</Tag></div>
              </Col>
              <Col xs={24} sm={8}>
                <Text strong>Status:</Text>
                <div>
                  <Tag color={selectedTemplatePreview.status?.toLowerCase() === 'approved' ? 'green' : 'orange'}>
                    {selectedTemplatePreview.status}
                  </Tag>
                </div>
              </Col>
            </Row>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div className="whatsapp-preview-container">
                {/* Chat Header */}
                <div className="chat-header">
                  <div className="chat-avatar whatsapp-avatar">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="20" cy="20" r="20" fill="#25D366"/>
                      <path d="M20 10C17.79 10 16 11.79 16 14C16 16.21 17.79 18 20 18C22.21 18 24 16.21 24 14C24 11.79 22.21 10 20 10ZM20 20C16.67 20 10 21.67 10 25V28H30V25C30 21.67 23.33 20 20 20Z" fill="white"/>
                    </svg>
                  </div>
                  <div className="chat-info">
                    <h3 className="chat-name">WhatsApp Business</h3>
                    <p className="chat-status">Template Preview</p>
                  </div>
                  <span className="template-category">{selectedTemplatePreview.templatecategory?.toUpperCase() || 'UTILITY'}</span>
                </div>

                {/* Chat Area */}
                <div className="chat-area">
                  <div className="message-container">
                    <div className="message-bubble">
                      {/* Header Content */}
                      {(() => {
                        const headerComponent = selectedTemplatePreview.component?.find(c => c.type === 'HEADER');
                        const mediaUrl = selectedTemplatePreview.header_media_url;
                        const format = selectedTemplatePreview.header_format || headerComponent?.format;
                        
                        if (mediaUrl && format === 'IMAGE') {
                          return (
                            <div className="message-header-content">
                              <img src={mediaUrl} alt="Template header" style={{ width: '100%', borderRadius: '8px 8px 0 0', maxHeight: 200, objectFit: 'cover', display: 'block' }} />
                            </div>
                          );
                        }
                        if (mediaUrl && format === 'VIDEO') {
                          return (
                            <div className="message-header-content">
                              <video src={mediaUrl} style={{ width: '100%', borderRadius: '8px 8px 0 0', maxHeight: 200, objectFit: 'cover', display: 'block' }} controls preload="metadata" />
                            </div>
                          );
                        }
                        if (mediaUrl && format === 'DOCUMENT') {
                          return (
                            <div className="message-header-content" style={{ padding: '12px', background: '#f0f4f8', borderRadius: '8px 8px 0 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: 28 }}>📄</span>
                              <a href={mediaUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#0036b0', wordBreak: 'break-all' }}>View Document</a>
                            </div>
                          );
                        }
                        if (headerComponent) {
                          return (
                            <div className="message-header-content">
                              {headerComponent.format === 'TEXT' && (
                                <div className="header-text">
                                  {headerComponent.text || 'Header text'}
                                </div>
                              )}
                              {headerComponent.format === 'IMAGE' && (
                                <div className="header-image">
                                  📷 Image Preview
                                </div>
                              )}
                              {headerComponent.format === 'VIDEO' && (
                                <div className="header-image">
                                  🎥 Video Preview
                                </div>
                              )}
                              {headerComponent.format === 'DOCUMENT' && (
                                <div className="header-image">
                                  📄 Document Preview
                                </div>
                              )}
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {/* Message Body */}
                      <div className="message-content">
                        {(() => {
                          const body = selectedTemplatePreview.component?.find(c => c.type === 'BODY');
                          return body?.text || selectedTemplatePreview.templatecontent || selectedTemplatePreview.content || '';
                        })()}
                      </div>

                      {/* Footer */}
                      {(() => {
                        const footer = selectedTemplatePreview.component?.find(c => c.type === 'FOOTER');
                        if (footer && footer.text) {
                          return (
                            <div className="message-footer-text">
                              {footer.text}
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {/* Message Footer with Time and Checkmarks */}
                      <div className="message-footer">
                        <div className="message-time">
                          {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          <svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.033l-.378.387a.32.32 0 0 0 .033.484l1.025.93a.32.32 0 0 0 .484-.033l6.272-8.048a.366.366 0 0 0-.063-.51zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033L3.724 9.587a.319.319 0 0 0-.484.033l-.378.387a.32.32 0 0 0 .033.484l1.025.93a.32.32 0 0 0 .484-.033L10.676 3.84a.366.366 0 0 0-.063-.51z" fill="#4fc3f7"/>
                          </svg>
                        </div>
                      </div>

                      {/* Buttons */}
                      {(() => {
                        const buttonsComp = selectedTemplatePreview.component?.find(c => c.type === 'BUTTONS');
                        if (buttonsComp && buttonsComp.buttons && Array.isArray(buttonsComp.buttons) && buttonsComp.buttons.length > 0) {
                          return (
                            <div className="template-buttons">
                              {buttonsComp.buttons.map((button, index) => (
                                <button key={index} className="template-button">
                                  {button.type === 'URL' && '🔗'}
                                  {button.type === 'PHONE_NUMBER' && '📞'}
                                  {button.type === 'QUICK_REPLY' && '💬'}
                                  {button.text || `Button ${index + 1}`}
                                </button>
                              ))}
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {selectedTemplatePreview.variables && selectedTemplatePreview.variables.length > 0 && (
              <div>
                <Divider>Variables ({selectedTemplatePreview.variables.length})</Divider>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {selectedTemplatePreview.variables.map(variable => (
                    <Tag key={variable} color="orange">{variable}</Tag>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Filter Modal */}
      {renderFilterModal()}

      {/* WhatsApp Pricing Calculator Modal */}
      <PricingCalculator visible={pricingCalcVisible} onClose={() => setPricingCalcVisible(false)} />
    </div>
  );
};

export default AdvancedBroadcastBuilder;
